<template>
  <div class="bg-gray-100 dark-bg-custom p-6 pt-0">
    <!-- Main Tabs -->
    <div class="sticky top-0 z-50 pt-4 bg-[#f3f4f6] dark-bg-custom">
      <div class="flex justify-between">
        <p class="text-md" v-if="isUploadCase">
          VERIFY MAPPING - <b>({{ editSubCase?.name }})</b>
        </p>
        <p class="text-md font-semibold" v-else>
          CREATE MAPPING - <b>({{ titleCase(useCase) }})</b>
        </p>
        <v-btn plain @click="onCancel">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>
      <v-alert color="blue" type="info" outlined class="text-sm font-medium">
        Here, we are considering the {{ titleCase(useCase) || 'Collection' }} use case. By default,
        naming conventions follow industry standards. However, they can be overridden and customized
        based on your company's requirements.
      </v-alert>
      <v-tabs v-model="mainTab">
        <v-tab class="tabtitle">Core Settings</v-tab>
        <v-tab class="tabtitle">Advanced Settings</v-tab>
        <v-tab class="tabtitle">
          <v-icon left small>mdi-finance</v-icon>
          Account Settings
        </v-tab>
      </v-tabs>
    </div>
    <v-divider></v-divider>
    <v-tabs-items v-model="mainTab">
      <!-- Core Settings Tab -->
      <v-tab-item>
        <v-form ref="form" v-model="valid" lazy-validation>
          <v-expansion-panels v-model="fieldPanel" flat>
            <!-- Field Mapping Panel -->

            <div class="w-full mt-4" flat>
              <v-card-text class="card-text">
                <!-- Replace the DataMapping component with conditional rendering -->
                <template v-if="showMappingDetails">
                  <v-tabs v-model="dataMappingTab" class="text-base" grow>
                    <v-tab>Sub-case Fields</v-tab>
                    <v-tab>Customer Fields</v-tab>
                  </v-tabs>

                  <v-tabs-items v-model="dataMappingTab">
                    <v-tab-item>
                      <v-text-field
                        v-model="caseType.value"
                        label="Subcase Name"
                        required
                        :rules="caseType.rules"
                        :disabled="!!isUploadCase"
                        class="mt-4"
                      ></v-text-field>
                      <v-data-table
                        :headers="[
                          { text: 'Field Name', value: 'text', width: '30%' },
                          { text: 'Mapping Name', value: 'value', width: '40%' },
                          { text: 'Visibility', value: 'settings', width: '15%' },
                          { text: 'Required', value: 'settings', width: '15%' }
                        ]"
                        :items="orgKeys"
                        hide-default-footer
                        dense
                        class="mt-4"
                      >
                        <template v-slot:item="{ item }">
                          <tr>
                            <td class="px-4 font-semibold">{{ item.text }}</td>
                            <td>
                              <div class="d-flex align-center">
                                <v-select
                                  v-if="item.text.includes('POS') || item.text.includes('EMI')"
                                  v-model="item.selectedPrefix"
                                  :items="['₹', '$', '€', '£', '¥', '₽']"
                                  dense
                                  class="mr-2"
                                  style="max-width: 80px"
                                  hide-details
                                  outlined
                                ></v-select>
                                <v-text-field
                                  v-model="item.value"
                                  dense
                                  :disabled="!!isUploadCase"
                                  hide-details
                                  outlined
                                ></v-text-field>
                                <v-select
                                  v-if="item.text.includes('Tenure')"
                                  v-model="item.selectedSuffix"
                                  :items="['Days', 'Months', 'Years']"
                                  dense
                                  outlined
                                  class="ml-2"
                                  style="max-width: 120px"
                                  hide-details
                                ></v-select>
                              </div>
                            </td>
                            <td>
                              <div class="ml-4">
                                <v-checkbox
                                  v-model="item.visible"
                                  dense
                                  hide-details
                                  class="mr-2"
                                ></v-checkbox>
                              </div>
                            </td>
                            <td>
                              <div>
                                <v-checkbox v-model="item.required" dense hide-details></v-checkbox>
                              </div>
                            </td>
                          </tr>
                        </template>
                      </v-data-table>

                      <v-divider class="my-4"></v-divider>

                      <v-expansion-panel flat>
                        <v-expansion-panel-header class="font-semibold"
                          >Custom Fields</v-expansion-panel-header
                        >
                        <v-expansion-panel-content>
                          <v-data-table
                            :headers="[
                              { text: 'Field Name', value: 'text', width: '40%' },
                              { text: 'Type / Options', value: 'type', width: '50%' },
                              { text: 'Actions', value: 'actions', sortable: false, width: '10%' }
                            ]"
                            :items="extraColumns"
                            hide-default-footer
                            dense
                          >
                            <template v-slot:item="{ item, index }">
                              <tr>
                                <td class="px-2 py-1">
                                  <v-text-field
                                    v-model="item.text"
                                    dense
                                    hide-details
                                    outlined
                                  ></v-text-field>
                                </td>
                                <td class="py-2">
                                  <v-select
                                    v-model="item.value"
                                    :items="getFilteredItems(index)"
                                    dense
                                    hide-details
                                    outlined
                                  ></v-select>
                                  <v-text-field
                                    v-if="item.value && item.value.includes('SELECT')"
                                    v-model="item.options"
                                    placeholder="Options (e.g. Low, Medium, High)"
                                    dense
                                    hide-details
                                    outlined
                                    class="mt-2"
                                  ></v-text-field>
                                </td>
                                <td class="px-4">
                                  <v-btn icon small color="error" @click="removeExtraColumn(index)">
                                    <v-icon small>mdi-delete</v-icon>
                                  </v-btn>
                                </td>
                              </tr>
                            </template>
                          </v-data-table>

                          <div class="flex justify-end">
                            <v-btn
                              color="primary"
                              text
                              small
                              outlined
                              class="mt-2"
                              @click="addExtraColumn"
                              v-if="!isUploadCase"
                            >
                              <v-icon left small>mdi-plus</v-icon>
                              Add
                            </v-btn>
                          </div>
                        </v-expansion-panel-content>
                      </v-expansion-panel>
                    </v-tab-item>

                    <v-tab-item>
                      <v-card flat>
                        <v-card-text>
                          <div class="d-flex flex-wrap">
                            <v-text-field
                              v-model="staticFields.crn"
                              label="CRN No"
                              required
                              :rules="staticFields.crnRules"
                              :disabled="!!isUploadCase"
                              class="mr-4"
                              outlined
                              dense
                              style="width: 200px"
                            ></v-text-field>
                            <v-text-field
                              v-model="staticFields.customerFullName"
                              label="Customer Name"
                              required
                              :rules="staticFields.customerFullNameRules"
                              :disabled="!!isUploadCase"
                              class="mr-4"
                              outlined
                              dense
                              style="width: 200px"
                            ></v-text-field>
                            <v-text-field
                              v-model="staticFields.customerMobile"
                              label="Mobile"
                              :disabled="!!isUploadCase"
                              class="mr-4"
                              style="width: 200px"
                              outlined
                              dense
                            ></v-text-field>
                            <v-text-field
                              v-model="staticFields.customerEmail"
                              label="Email"
                              :disabled="!!isUploadCase"
                              style="width: 200px"
                              outlined
                              dense
                            ></v-text-field>
                          </div>

                          <!-- Address section -->
                          <v-expansion-panels class="mt-4">
                            <v-expansion-panel v-for="(address, index) in addresses" :key="index">
                              <v-expansion-panel-header>
                                <div class="d-flex align-center">
                                  <span>Address {{ index + 1 }}</span>
                                  <v-chip
                                    x-small
                                    label
                                    class="ml-2"
                                    :color="address.default ? 'primary' : 'grey'"
                                  >
                                    {{ address.typeOfAddress || 'Select Type' }}
                                  </v-chip>
                                </div>
                              </v-expansion-panel-header>
                              <v-expansion-panel-content>
                                <div class="d-flex justify-between align-center mb-4">
                                  <v-select
                                    v-model="address.typeOfAddress"
                                    :items="getFilteredAddressTypes(index)"
                                    label="Type"
                                    :disabled="!!isUploadCase"
                                    class="mr-4"
                                    style="max-width: 300px"
                                    outlined
                                    dense
                                    hide-details
                                  ></v-select>
                                  <v-checkbox
                                    v-model="address.default"
                                    label="Set as default"
                                    @change="setDefault(index)"
                                    class="mr-4"
                                  ></v-checkbox>
                                  <v-btn
                                    icon
                                    small
                                    color="error"
                                    @click="removeAddress(index)"
                                    v-show="addresses.length > 1"
                                  >
                                    <v-icon small>mdi-delete</v-icon>
                                  </v-btn>
                                </div>

                                <div class="d-flex flex-wrap">
                                  <v-text-field
                                    v-for="(field, key) in addressFields"
                                    :key="key"
                                    v-model="address[key].displayName"
                                    :label="field.text"
                                    :disabled="!!isUploadCase"
                                    class="mr-4 mb-4"
                                    style="width: 200px"
                                    @input="validateAddress"
                                    outlined
                                    dense
                                    hide-details
                                  ></v-text-field>
                                </div>
                              </v-expansion-panel-content>
                            </v-expansion-panel>
                          </v-expansion-panels>

                          <div class="flex justify-end mt-4">
                            <v-btn
                              color="primary"
                              text
                              small
                              outlined
                              @click="addAddress"
                              v-show="addresses.length < addressTypes.length"
                            >
                              <v-icon left small>mdi-plus</v-icon>
                              Add Address
                            </v-btn>
                          </div>
                        </v-card-text>
                      </v-card>
                    </v-tab-item>
                  </v-tabs-items>
                </template>
                <template v-else>
                  <DataMapping
                    :caseType="caseType"
                    :orgKeys="orgKeys"
                    :extraColumns="extraColumns"
                    :staticFields="staticFields"
                    :addresses="addresses"
                    :addressFields="addressFields"
                    :addressTypes="addressTypes"
                    :isUploadCase="isUploadCase"
                    @remove-extra-column="removeExtraColumn"
                    @add-extra-column="addExtraColumn"
                    @remove-address="removeAddress"
                    @add-address="addAddress"
                    @set-default-address="setDefault"
                    @validate-address="checkForDuplicateAddressLine1"
                    @icon-selected="onSubcaseIconSelected"
                  />
                </template>
              </v-card-text>
            </div>
          </v-expansion-panels>
          <v-divider class=""></v-divider>

          <v-expansion-panels v-model="statusPanel" flat>
            <!-- Case Status Panel -->
            <v-card class="w-full mt-4" flat>
              <v-card-title class="pb-2 text-base normal-case">
                <v-icon class="mr-2" color="primary">mdi-state-machine</v-icon>
                Define States for Your Sub-Case Journey
              </v-card-title>
              <v-card-text>
                <p>
                  Create the possible outcomes for this sub-case and design the workflow for each.
                </p>
                <v-expansion-panel v-model="panel" flat>
                  <v-expansion-panel-header>
                    <div>
                      <span>Outcomes</span>
                      <div>
                        <span class="text-xs font-medium text-gray-600"
                          >(Specify the different result states your sub-case can reach.)</span
                        >
                      </div>
                    </div>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <div class="d-flex align-center justify-space-between mb-4">
                      <v-text-field
                        v-model="caseOutcomeStatus"
                        label="Outcome Column"
                        :disabled="!!isUploadCase"
                        required
                        dense
                        outlined
                        hide-details
                        class="mr-4"
                      >
                        <template v-slot:append>
                          <v-tooltip top>
                            <template v-slot:activator="{ on, attrs }">
                              <v-icon
                                v-bind="attrs"
                                v-on="on"
                                class="info-icon"
                                :color="$vuetify.theme.currentTheme.primary"
                              >
                                mdi-information-outline
                              </v-icon>
                            </template>
                            <span
                              >The binded text will be displayed in the teams/cases table and the
                              same text will be used of update/create the case data.</span
                            >
                          </v-tooltip>
                        </template>
                      </v-text-field>
                      <v-switch
                        v-model="enableOutcomeColumn"
                        label="Enable"
                        dense
                        hide-details
                        class="mt-0"
                      ></v-switch>
                    </div>
                    <CaseOutcomeStatus
                      :categories="categories"
                      :removeCategory="removeCategory"
                      :addCategory="addCategory"
                      :isUploadCase="isUploadCase"
                      v-model="caseOutcomeStatus"
                    />
                  </v-expansion-panel-content>
                </v-expansion-panel>
                <!-- <v-expansion-panel v-model="panel" flat v-show="!isUploadCase">
                  <v-expansion-panel-header class="px-4">
                    <div>
                      <span> Create Questionnaire (for each Outcome individually) </span>
                      <div>
                        <span class="text-xs font-medium text-gray-600"
                          >(Design a custom questionnaire for each outcome to capture relevant
                          information.)</span
                        >
                      </div>
                    </div>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <div v-if="catOptionTabs && catOptionTabs.length">
                      <v-tabs v-model="tabs" align-with-title>
                        <v-tabs-slider color="blue"></v-tabs-slider>
                        <v-tab v-for="item in catOptionTabs" :key="item" class="ml-0">
                          {{ item }}
                        </v-tab>
                      </v-tabs>
                      <v-tabs-items v-model="tabs">
                        <v-tab-item v-for="(item, index) in catOptionTabs" :key="item">
                          <div class="mt-4">
                            <Questionnaire
                              :questionTypes="allowedQuestionsTypes"
                              @questions="(val) => handleQuestions(val, index)"
                              :orgMapping="'orgMapping'"
                              :subCaseQuestions="subCaseQuestions[item]"
                              :outcomeQuestions="
                                getFilteredOutcomeQuestions(subCaseQuestions, item)
                              "
                              @clonedQuestions="
                                (val) => handleCloneAction(val, item, 'subCaseQuestions')
                              "
                            />
                          </div>
                        </v-tab-item>
                      </v-tabs-items>
                    </div>
                    <div v-else>
                      <v-alert color="orange" type="warning" text>
                        Add Outcome first to create questions
                      </v-alert>
                    </div>
                  </v-expansion-panel-content>
                </v-expansion-panel>

                <v-expansion-panel v-model="panel" flat v-show="!isUploadCase">
                  <v-expansion-panel-header class="px-4">
                    <div>
                      Create Customer feedback form (for each Outcome individually)
                      <div>
                        <span class="text-xs font-medium text-gray-600"
                          >(Set up a feedback form for each outcome to gather customer insights and
                          improve your process.)</span
                        >
                      </div>
                    </div>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <div v-if="isWhatsAppEnabled">
                      <div v-if="catOptionTabs && catOptionTabs.length">
                        <v-tabs v-model="tabs" align-with-title>
                          <v-tabs-slider color="blue"></v-tabs-slider>
                          <v-tab v-for="item in catOptionTabs" :key="item" class="ml-0">
                            {{ item }}
                          </v-tab>
                        </v-tabs>
                        <v-tabs-items v-model="tabs">
                          <v-tab-item v-for="(item, index) in catOptionTabs" :key="item">
                            <div class="mt-4">
                              <Questionnaire
                                :questionTypes="allowedQuestionsTypesCustomerFeedback"
                                @questions="(val) => handleFeedback(val, index)"
                                :orgMapping="'orgMapping'"
                                :subCaseQuestions="customerFeedback[item]"
                                :outcomeQuestions="
                                  getFilteredOutcomeQuestions(customerFeedback, item)
                                "
                                @clonedQuestions="
                                  (val) => handleCloneAction(val, item, 'customerFeedback')
                                "
                              />
                            </div>
                          </v-tab-item>
                        </v-tabs-items>
                      </div>
                      <div v-else>
                        <v-alert color="orange" type="warning" text>
                          Add Outcome first to create questions
                        </v-alert>
                      </div>
                    </div>
                    <div v-else>
                      <v-alert color="orange" type="warning" text>
                        Whatsapp integration is not enabled. Integrate whatsapp business account in
                        cualitywork's platform-integration.
                      </v-alert>
                    </div>
                  </v-expansion-panel-content>
                </v-expansion-panel> -->
              </v-card-text>
            </v-card>
          </v-expansion-panels>
        </v-form>
      </v-tab-item>

      <!-- Advanced Settings Tab -->
      <v-tab-item>
        <div class="p-4">
          <!-- Advanced Settings Selection -->
          <div class="mb-6">
            <v-card outlined>
              <v-card-title class="pb-2 text-base">
                <v-icon class="mr-2" color="primary">mdi-cog-outline</v-icon>
                Advanced Settings Configuration
              </v-card-title>
              <v-card-text>
                <!-- Settings Selection Checkboxes -->
                <div class="grid grid-cols-1 gap-4">
                  <!-- Branch Mapping Checkbox -->
                  <div class="flex items-center" v-if="branches.length">
                    <v-checkbox
                      v-model="enableBranchMapping"
                      :color="$vuetify.theme.currentTheme.primary"
                      class="mt-0 pt-0"
                      :disabled="!!isUploadCase"
                    ></v-checkbox>
                    <div class="ml-2">
                      <p class="font-semibold mb-1">Branch Linking</p>
                      <p class="text-sm text-gray-600 mb-0">
                        Associate this sub-case with one or more branches of your organization.
                      </p>
                    </div>
                  </div>
                  <!-- Branch Mapping Section -->
                  <div v-if="enableBranchMapping && branches.length">
                    <v-select
                      v-model="selectedBranch"
                      :items="branches"
                      item-value="_id"
                      item-text="branchName"
                      label="Select Branch(s)"
                      :disabled="!!isUploadCase"
                      class="pl-10"
                      clearable
                    ></v-select>
                  </div>

                  <!-- Entity Mapping Checkbox -->
                  <div class="flex items-center">
                    <v-checkbox
                      v-model="enableEntityMapping"
                      :color="$vuetify.theme.currentTheme.primary"
                      class="mt-0 pt-0"
                      :disabled="!!isUploadCase"
                    ></v-checkbox>
                    <div class="ml-2">
                      <p class="font-semibold mb-1">Entity / Group Assignment</p>
                      <p class="text-sm text-gray-600 mb-0">
                        Organize sub-case and customer data by assigning them to a custom group or
                        entity.
                      </p>
                    </div>
                  </div>

                  <!-- Entity Mapping Section -->
                  <div v-if="enableEntityMapping">
                    <div class="flex justify-between">
                      <v-text-field
                        v-model="entityName"
                        label="Enter Entity / Group Name"
                        required
                        :disabled="!!isUploadCase"
                        hide-details
                        class="w-3/4 pl-10"
                      >
                      </v-text-field>
                      <!-- <div class="flex justify-end gap-4 mb-3">
                        <v-switch
                          v-model="entityEnabled"
                          label="Enable"
                          class=""
                          hide-details
                        ></v-switch>
                      </div> -->
                    </div>
                  </div>

                  <!-- Data Category Mapping Checkbox -->
                  <div class="flex items-center">
                    <v-checkbox
                      v-model="enableDataCategoryMapping"
                      :color="$vuetify.theme.currentTheme.primary"
                      class="mt-0 pt-0"
                      :disabled="!!isUploadCase"
                    ></v-checkbox>
                    <div class="ml-2">
                      <p class="font-semibold mb-1">Data Categorization</p>
                      <p class="text-sm text-gray-600 mb-0">
                        Classify sub-case data into custom categories for better segmentation and
                        reporting.
                      </p>
                    </div>
                  </div>

                  <!-- Data Category Mapping Section -->
                  <div v-if="enableDataCategoryMapping">
                    <v-combobox
                      v-model="addedDataCategories"
                      :items="dataCategorizations"
                      item-text="text"
                      item-value="value"
                      multiple
                      chips
                      small-chips
                      label="Data Category Options"
                      :disabled="!!isUploadCase"
                      class="pl-10"
                    >
                      <template v-slot:selection="{ item }">
                        <v-chip>
                          {{ item }}
                          <v-icon
                            small
                            @click="removeDataCategory(item)"
                            color="red"
                            class="ml-2"
                            v-tooltip="{
                              text: 'Delete'
                            }"
                          >
                            $delete
                          </v-icon>
                        </v-chip>
                      </template>
                    </v-combobox>
                  </div>

                  <!-- Loan Type Mapping Checkbox -->
                  <div class="flex items-center" v-if="useCase.toLowerCase() === 'loan_management'">
                    <v-checkbox
                      v-model="enableLoanTypeMapping"
                      :color="$vuetify.theme.currentTheme.primary"
                      class="mt-0 pt-0"
                      :disabled="!!isUploadCase"
                    ></v-checkbox>
                    <div class="ml-2">
                      <p class="font-semibold mb-1">Loan Types</p>
                      <p class="text-sm text-gray-600 mb-0">
                        Categorize this sub-case into one or more loan types.
                      </p>
                    </div>
                  </div>

                  <!-- Sub Loan Mapping Section -->
                  <div v-if="enableLoanTypeMapping && useCase.toLowerCase() === 'loan_management'">
                    <v-autocomplete
                      v-model="selectedLoanTypes"
                      :items="loanTypeOptions"
                      multiple
                      chips
                      small-chips
                      label="Select Loan Type(s)"
                      :disabled="!!isUploadCase"
                      class="pl-10"
                      outlined
                      dense
                    >
                      <template v-slot:prepend-item>
                        <v-list-item ripple @click="toggleAllLoanTypes">
                          <v-list-item-action>
                            <v-icon :color="selectedLoanTypes.length > 0 ? 'primary' : ''">
                              {{ allLoansSelectedIcon }}
                            </v-icon>
                          </v-list-item-action>
                          <v-list-item-content>
                            <v-list-item-title> Select All </v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                        <v-divider class="mt-2"></v-divider>
                      </template>
                      <template v-slot:item="{ item, on, attrs }">
                        <v-list-item v-on="on" v-bind="attrs" ripple>
                          <v-list-item-action>
                            <v-checkbox
                              :input-value="selectedLoanTypes.includes(item)"
                              color="primary"
                              hide-details
                            ></v-checkbox>
                          </v-list-item-action>
                          <v-list-item-content>
                            <v-list-item-title>{{ item }}</v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                      </template>
                      <template v-slot:selection="{ item, index }">
                        <v-chip v-if="index < 3" small label outlined color="primary">
                          <span>{{ item }}</span>
                        </v-chip>
                        <span v-if="index === 3" class="grey--text text-caption ml-2">
                          (+{{ selectedLoanTypes.length - 3 }} others)
                        </span>
                      </template>
                    </v-autocomplete>
                  </div>

                  <!-- Loan Category Mapping Checkbox -->
                  <div class="flex items-center" v-if="useCase.toLowerCase() === 'loan_management'">
                    <v-checkbox
                      v-model="enableLoanCategoryMapping"
                      :color="$vuetify.theme.currentTheme.primary"
                      class="mt-0 pt-0"
                      :disabled="!!isUploadCase"
                    ></v-checkbox>
                    <div class="ml-2">
                      <p class="font-semibold mb-1">Loan Category Mapping</p>
                      <p class="text-sm text-gray-600 mb-0">
                        Categorize this sub-case into one or more loan categories.
                      </p>
                    </div>
                  </div>

                  <!-- Loan Category Mapping Section -->
                  <div
                    v-if="enableLoanCategoryMapping && useCase.toLowerCase() === 'loan_management'"
                  >
                    <v-combobox
                      v-model="selectedLoanCategories"
                      :items="loanCategoryOptions"
                      multiple
                      chips
                      small-chips
                      label="Select Loan Category(s)"
                      :disabled="!!isUploadCase"
                      class="pl-10"
                      outlined
                      dense
                      hide-selected
                      hint="Type and press enter to add a new category"
                      persistent-hint
                      @input="updateLoanCategoryOptions"
                    >
                      <template v-slot:prepend-item>
                        <v-list-item ripple @click="toggleAllLoanCategories">
                          <v-list-item-action>
                            <v-icon :color="selectedLoanCategories.length > 0 ? 'primary' : ''">
                              {{ allLoanCategoriesSelectedIcon }}
                            </v-icon>
                          </v-list-item-action>
                          <v-list-item-content>
                            <v-list-item-title> Select All </v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                        <v-divider class="mt-2"></v-divider>
                      </template>
                      <template v-slot:item="{ item, on, attrs }">
                        <v-list-item v-on="on" v-bind="attrs" ripple>
                          <v-list-item-action>
                            <v-checkbox
                              :input-value="selectedLoanCategories.includes(item)"
                              color="primary"
                              hide-details
                            ></v-checkbox>
                          </v-list-item-action>
                          <v-list-item-content>
                            <v-list-item-title>{{ item }}</v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                      </template>
                      <template v-slot:selection="{ item, index }">
                        <v-chip v-if="index < 3" small label outlined color="primary">
                          <span>{{ item }}</span>
                        </v-chip>
                        <span v-if="index === 3" class="grey--text text-caption ml-2">
                          (+{{ selectedLoanCategories.length - 3 }} others)
                        </span>
                      </template>
                    </v-combobox>
                  </div>

                  <!-- Lead Source Mapping Checkbox -->
                  <div class="flex items-center">
                    <v-checkbox
                      v-model="enableLeadSourceMapping"
                      :color="$vuetify.theme.currentTheme.primary"
                      class="mt-0 pt-0"
                      :disabled="!!isUploadCase"
                    ></v-checkbox>
                    <div class="ml-2">
                      <p class="font-semibold mb-1">Lead Source Mapping</p>
                      <p class="text-sm text-gray-600 mb-0">
                        Categorize this sub-case into one or more lead sources.
                      </p>
                    </div>
                  </div>

                  <!-- Lead Source Mapping Section -->
                  <div v-if="enableLeadSourceMapping">
                    <v-autocomplete
                      v-model="selectedLeadSources"
                      :items="leadSourceOptions"
                      multiple
                      chips
                      small-chips
                      label="Select Lead Source(s)"
                      :disabled="!!isUploadCase"
                      class="pl-10"
                      outlined
                      dense
                    >
                      <template v-slot:prepend-item>
                        <v-list-item ripple @click="toggleAllLeadSources">
                          <v-list-item-action>
                            <v-icon :color="selectedLeadSources.length > 0 ? 'primary' : ''">
                              {{ allLeadSourcesSelectedIcon }}
                            </v-icon>
                          </v-list-item-action>
                          <v-list-item-content>
                            <v-list-item-title> Select All </v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                        <v-divider class="mt-2"></v-divider>
                      </template>
                      <template v-slot:item="{ item, on, attrs }">
                        <v-list-item v-on="on" v-bind="attrs" ripple>
                          <v-list-item-action>
                            <v-checkbox
                              :input-value="selectedLeadSources.includes(item)"
                              color="primary"
                              hide-details
                            ></v-checkbox>
                          </v-list-item-action>
                          <v-list-item-content>
                            <v-list-item-title>{{ item }}</v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                      </template>
                      <template v-slot:selection="{ item, index }">
                        <v-chip v-if="index < 3" small label outlined color="primary">
                          <span>{{ item }}</span>
                        </v-chip>
                        <span v-if="index === 3" class="grey--text text-caption ml-2">
                          (+{{ selectedLeadSources.length - 3 }} others)
                        </span>
                      </template>
                    </v-autocomplete>
                  </div>
                  <!-- Sub-Case Cross Linking Checkbox -->
                  <div class="flex items-center">
                    <v-checkbox
                      v-model="enableCrossLinking"
                      :color="$vuetify.theme.currentTheme.primary"
                      class="mt-0 pt-0"
                      :disabled="!!isUploadCase"
                    ></v-checkbox>
                    <div class="ml-2">
                      <p class="font-semibold mb-1">Sub-Case Cross Linking</p>
                      <p class="text-sm text-gray-600 mb-0">
                        Link this sub-case with another sub-case for cross-module functionality.
                      </p>
                    </div>
                  </div>

                  <!-- Cross Linking Section -->
                  <div v-if="enableCrossLinking" class="mb-4">
                    <v-select
                      v-model="crossLinkedSubCaseId"
                      :items="categorizedSubCases"
                      item-text="name"
                      item-value="_id"
                      label="Select Sub-Case to Link"
                      :disabled="!!isUploadCase"
                      class="pl-10"
                      outlined
                      dense
                      clearable
                    >
                      <template v-slot:item="{ item }">
                        <v-list-item-content>
                          <v-list-item-title>{{ item.name }}</v-list-item-title>
                          <v-list-item-subtitle class="text-xs text-gray-500 uppercase">{{
                            formatUseCaseName(item.useCase)
                          }}</v-list-item-subtitle>
                        </v-list-item-content>
                      </template>
                    </v-select>
                  </div>
                </div>
              </v-card-text>
              <v-card-title class="pb-2 text-base">
                <v-icon class="mr-2" color="primary">mdi-cog-outline</v-icon>
                Set Naming Conventions
              </v-card-title>
              <v-card-text>
                <p class="px-4">
                  Define the primary status key for your workflow and customize tab names to match
                  your organization's terminology.
                </p>

                <div class="pl-4">
                  <v-alert type="info" color="blue" dense outlined class="text-sm font-medium">
                    The case status column value should be
                    <strong>OPEN, CLOSE, or IN_PROGRESS</strong>.
                  </v-alert>
                  <v-text-field
                    v-model="caseStatusName"
                    label="Case Status Column Name"
                    :disabled="!!isUploadCase"
                    required
                    hide-details
                    outlined
                    dense
                    class="mb-4"
                  ></v-text-field>
                  <v-combobox
                    v-model="caseStatusesInput"
                    multiple
                    chips
                    small-chips
                    deletable-chips
                    label="Define Case Statuses"
                    outlined
                    dense
                    hide-selected
                    hint="Type a status name (e.g., Pending Review) and press Enter to add"
                    persistent-hint
                    :disabled="!!isUploadCase"
                  ></v-combobox>
                </div>
                <div>
                  <v-card-title class="mt-4 text-base card-title">
                    <v-icon class="mr-2" color="primary">mdi-table</v-icon>
                    Table Mappings
                  </v-card-title>
                  <div class="pl-4">
                    <v-alert color="blue" type="info" dense outlined class="text-sm font-medium">
                      Below Tabs display in
                      <strong> {{ titleCase(useCase) }} </strong>
                      ->
                      <strong> {Sub-case} </strong>
                    </v-alert>
                  </div>
                  <div class="bg-white border rounded overflow-hidden ml-4">
                    <div class="flex border-b">
                      <div
                        v-for="(tab, index) in tableTabs"
                        :key="index"
                        class="px-4 py-3 font-medium text-sm cursor-pointer transition-colors"
                        :class="[
                          index === 0
                            ? 'text-green-600 border-b-2 border-green-500'
                            : 'text-gray-600 hover:bg-gray-100'
                        ]"
                      >
                        <v-icon left>{{ tab.icon }}</v-icon>
                        {{ tab.text }}
                      </div>
                      <div class="p-2 flex justify-end">
                        <v-btn class="primary">
                          <v-icon left>mdi-refresh</v-icon>
                          Reset</v-btn
                        >
                      </div>
                    </div>
                    <div class="p-4 bg-gray-50">
                      <div class="text-sm text-gray-500 text-center">
                        <div class="grid grid-cols-2 lg:grid-cols-3 gap-4">
                          <div v-for="(key, i) in tableTabKeys" :key="i" class="mb-5 w-full">
                            <v-text-field
                              v-model="key.value"
                              :label="key.text"
                              :disabled="!!isUploadCase"
                              required
                              outlined
                              dense
                              hide-details
                              :prepend-inner-icon="getDisplayIcon(key)"
                              :loading="iconButtonLoading[key.key]"
                              @click:prepend-inner="handleIconSelector('open', key)"
                              class="flex-grow"
                            >
                              <template v-slot:append>
                                <v-tooltip top>
                                  <template v-slot:activator="{ on, attrs }">
                                    <div v-on="on" v-bind="attrs" class="flex items-center h-full">
                                      <v-switch
                                        v-model="key.visible"
                                        color="primary"
                                        dense
                                        hide-details
                                        class="mt-0 pt-0"
                                      ></v-switch>
                                    </div>
                                  </template>
                                  <span>{{ key.visible ? 'Hide from UI' : 'Show in UI' }}</span>
                                </v-tooltip>
                              </template>
                            </v-text-field>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Icon Selector Dialog -->
                <IconSelector
                  v-model="iconSelectorDialog"
                  :current-icon="getCurrentEditingIcon()"
                  :icons="apiIcons"
                  :loading="iconSelectorLoading"
                  @icon-selected="onIconSelected"
                  @cancelled="onIconCancelled"
                />
              </v-card-text>
            </v-card>
          </div>
        </div>
      </v-tab-item>
      <!-- Account Settings Tab (with nested sub-tabs) -->
      <v-tab-item>
        <div class="pa-4 bg-white border rounded-lg mt-4 shadow-sm">
          <v-tabs v-model="accountSubTab" class="border-b" color="primary" dense>
            <v-tab class="text-[11px] font-semibold uppercase tracking-wider">
              <v-icon left small>mdi-cog-outline</v-icon>
              General
            </v-tab>
            <v-tab class="text-[11px] font-semibold uppercase tracking-wider">
              <v-icon left small>mdi-bank-transfer</v-icon>
              Auto Posting
            </v-tab>
            <v-tab class="text-[11px] font-semibold uppercase tracking-wider">
              <v-icon left small>mdi-percent-box-outline</v-icon>
              Tax Settings
            </v-tab>
            <v-tab class="text-[11px] font-semibold uppercase tracking-wider">
              <v-icon left small>mdi-cash-register</v-icon>
              Charges / VAS Posting
            </v-tab>
          </v-tabs>

          <v-tabs-items v-model="accountSubTab" class="mt-4">
            <!-- 1. General Settings Sub-tab -->
            <v-tab-item>
              <GeneralSettings v-model="generalSettings" />
              <div class="flex justify-end gap-4 px-4 pb-4">
                <v-btn elevation="2" class="normal-case" small text @click="onCancel">Cancel</v-btn>
                <v-btn
                  depressed
                  @click="Array.isArray(useCaseLabel) ? onSubmit() : createExtendUseCase()"
                  color="primary"
                  elevation="2"
                  small
                  class="white--text normal-case"
                >
                  <v-icon left>mdi-content-save</v-icon>
                  Save General Settings
                </v-btn>
              </div>
            </v-tab-item>

            <!-- 2. Auto Posting Settings Sub-tab -->
            <v-tab-item>
              <div class="p-4 pt-2">
                <div class="mb-4">
                  <p class="text-sm text-gray-500 mb-4">
                    Link your accounting ledger accounts to specific loan transaction types for
                    automatic journal posting.
                  </p>
                </div>

                <!-- Account Search Grid -->
                <v-row>
                  <v-col cols="12" md="6">
                    <v-card outlined class="pa-4 rounded-lg h-full">
                      <div class="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-4">
                        Loan &amp; Interest Ledgers
                      </div>

                      <!-- 1. Loan Group -->
                      <div
                        v-if="
                          autoPostingSettings.loanGroup &&
                          autoPostingSettings.loanGroup.name &&
                          !editModes.loanGroup
                        "
                        class="d-flex align-center border rounded px-3 py-2 mb-3 bg-gray-50"
                      >
                        <v-icon color="primary" class="mr-2">mdi-bank</v-icon>
                        <div class="flex-grow-1">
                          <div class="text-xs text-gray-400">Loan Group</div>
                          <div class="text-sm font-semibold">
                            {{ autoPostingSettings.loanGroup.name }}
                          </div>
                        </div>
                        <v-btn icon small @click="editModes.loanGroup = true" class="mr-1">
                          <v-icon small color="grey darken-1">mdi-pencil</v-icon>
                        </v-btn>
                        <v-btn
                          icon
                          small
                          @click="
                            autoPostingSettings.loanGroup = null
                            editModes.loanGroup = false
                          "
                        >
                          <v-icon small color="red darken-1">mdi-close</v-icon>
                        </v-btn>
                      </div>
                      <v-autocomplete
                        v-else
                        v-model="autoPostingSettings.loanGroup"
                        :items="accountsList"
                        :search-input.sync="accountSearchLoanGroup"
                        @update:search-input="
                          (val) => val && val.length > 1 && debouncedFetchAccounts(val)
                        "
                        @change="
                          (val) => {
                            if (val) {
                              autoPostingSettings.loanGroup = { _id: val._id, name: val.name }
                              editModes.loanGroup = false
                            }
                          }
                        "
                        return-object
                        item-text="name"
                        item-value="_id"
                        label="Loan Group"
                        outlined
                        dense
                        class="mb-3"
                        clearable
                        hide-details
                        prepend-inner-icon="mdi-bank"
                        :loading="accountsLoading"
                        :filter="() => true"
                        no-data-text="Type to search accounts..."
                      ></v-autocomplete>

                      <!-- 2. Interest Ledger -->
                      <div
                        v-if="
                          autoPostingSettings.interest &&
                          autoPostingSettings.interest.name &&
                          !editModes.interest
                        "
                        class="d-flex align-center border rounded px-3 py-2 mb-3 bg-gray-50"
                      >
                        <v-icon color="primary" class="mr-2">mdi-percent</v-icon>
                        <div class="flex-grow-1">
                          <div class="text-xs text-gray-400">Interest Ledger</div>
                          <div class="text-sm font-semibold">
                            {{ autoPostingSettings.interest.name }}
                          </div>
                        </div>
                        <v-btn icon small @click="editModes.interest = true" class="mr-1">
                          <v-icon small color="grey darken-1">mdi-pencil</v-icon>
                        </v-btn>
                        <v-btn
                          icon
                          small
                          @click="
                            autoPostingSettings.interest = null
                            editModes.interest = false
                          "
                        >
                          <v-icon small color="red darken-1">mdi-close</v-icon>
                        </v-btn>
                      </div>
                      <v-autocomplete
                        v-else
                        v-model="autoPostingSettings.interest"
                        :items="accountsList"
                        :search-input.sync="accountSearchInterest"
                        @update:search-input="
                          (val) => val && val.length > 1 && debouncedFetchAccounts(val)
                        "
                        @change="
                          (val) => {
                            if (val) {
                              autoPostingSettings.interest = { _id: val._id, name: val.name }
                              editModes.interest = false
                            }
                          }
                        "
                        return-object
                        item-text="name"
                        item-value="_id"
                        label="Interest Ledger"
                        outlined
                        dense
                        class="mb-3"
                        clearable
                        hide-details
                        prepend-inner-icon="mdi-percent"
                        :loading="accountsLoading"
                        :filter="() => true"
                        no-data-text="Type to search accounts..."
                      ></v-autocomplete>

                      <!-- 3. LP Interest -->
                      <div
                        v-if="
                          autoPostingSettings.lpInterest &&
                          autoPostingSettings.lpInterest.name &&
                          !editModes.lpInterest
                        "
                        class="d-flex align-center border rounded px-3 py-2 mb-3 bg-gray-50"
                      >
                        <v-icon color="primary" class="mr-2">mdi-percent-outline</v-icon>
                        <div class="flex-grow-1">
                          <div class="text-xs text-gray-400">LP Interest</div>
                          <div class="text-sm font-semibold">
                            {{ autoPostingSettings.lpInterest.name }}
                          </div>
                        </div>
                        <v-btn icon small @click="editModes.lpInterest = true" class="mr-1">
                          <v-icon small color="grey darken-1">mdi-pencil</v-icon>
                        </v-btn>
                        <v-btn
                          icon
                          small
                          @click="
                            autoPostingSettings.lpInterest = null
                            editModes.lpInterest = false
                          "
                        >
                          <v-icon small color="red darken-1">mdi-close</v-icon>
                        </v-btn>
                      </div>
                      <v-autocomplete
                        v-else
                        v-model="autoPostingSettings.lpInterest"
                        :items="accountsList"
                        :search-input.sync="accountSearchLpInterest"
                        @update:search-input="
                          (val) => val && val.length > 1 && debouncedFetchAccounts(val)
                        "
                        @change="
                          (val) => {
                            if (val) {
                              autoPostingSettings.lpInterest = { _id: val._id, name: val.name }
                              editModes.lpInterest = false
                            }
                          }
                        "
                        return-object
                        item-text="name"
                        item-value="_id"
                        label="LP Interest"
                        outlined
                        dense
                        class="mb-3"
                        clearable
                        hide-details
                        prepend-inner-icon="mdi-percent-outline"
                        :loading="accountsLoading"
                        :filter="() => true"
                        no-data-text="Type to search accounts..."
                      ></v-autocomplete>

                      <!-- 4. HandLoan LP Interest -->
                      <div
                        v-if="
                          autoPostingSettings.handLoanLpInterest &&
                          autoPostingSettings.handLoanLpInterest.name &&
                          !editModes.handLoanLpInterest
                        "
                        class="d-flex align-center border rounded px-3 py-2 mb-3 bg-gray-50"
                      >
                        <v-icon color="primary" class="mr-2">mdi-hand-coin-outline</v-icon>
                        <div class="flex-grow-1">
                          <div class="text-xs text-gray-400">HandLoan LP Interest</div>
                          <div class="text-sm font-semibold">
                            {{ autoPostingSettings.handLoanLpInterest.name }}
                          </div>
                        </div>
                        <v-btn icon small @click="editModes.handLoanLpInterest = true" class="mr-1">
                          <v-icon small color="grey darken-1">mdi-pencil</v-icon>
                        </v-btn>
                        <v-btn
                          icon
                          small
                          @click="
                            autoPostingSettings.handLoanLpInterest = null
                            editModes.handLoanLpInterest = false
                          "
                        >
                          <v-icon small color="red darken-1">mdi-close</v-icon>
                        </v-btn>
                      </div>
                      <v-autocomplete
                        v-else
                        v-model="autoPostingSettings.handLoanLpInterest"
                        :items="accountsList"
                        :search-input.sync="accountSearchHandLoanLp"
                        @update:search-input="
                          (val) => val && val.length > 1 && debouncedFetchAccounts(val)
                        "
                        @change="
                          (val) => {
                            if (val) {
                              autoPostingSettings.handLoanLpInterest = {
                                _id: val._id,
                                name: val.name
                              }
                              editModes.handLoanLpInterest = false
                            }
                          }
                        "
                        return-object
                        item-text="name"
                        item-value="_id"
                        label="HandLoan LP Interest"
                        outlined
                        dense
                        class="mb-0"
                        clearable
                        hide-details
                        prepend-inner-icon="mdi-hand-coin-outline"
                        :loading="accountsLoading"
                        :filter="() => true"
                        no-data-text="Type to search accounts..."
                      ></v-autocomplete>
                    </v-card>
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-card outlined class="pa-4 rounded-lg h-full">
                      <div class="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-4">
                        Charges &amp; Adjustments
                      </div>

                      <!-- 5. Bad Debts Ledger -->
                      <div
                        v-if="
                          autoPostingSettings.badDebts &&
                          autoPostingSettings.badDebts.name &&
                          !editModes.badDebts
                        "
                        class="d-flex align-center border rounded px-3 py-2 mb-3 bg-gray-50"
                      >
                        <v-icon color="primary" class="mr-2">mdi-alert-circle-outline</v-icon>
                        <div class="flex-grow-1">
                          <div class="text-xs text-gray-400">Bad Debts Ledger</div>
                          <div class="text-sm font-semibold">
                            {{ autoPostingSettings.badDebts.name }}
                          </div>
                        </div>
                        <v-btn icon small @click="editModes.badDebts = true" class="mr-1">
                          <v-icon small color="grey darken-1">mdi-pencil</v-icon>
                        </v-btn>
                        <v-btn
                          icon
                          small
                          @click="
                            autoPostingSettings.badDebts = null
                            editModes.badDebts = false
                          "
                        >
                          <v-icon small color="red darken-1">mdi-close</v-icon>
                        </v-btn>
                      </div>
                      <v-autocomplete
                        v-else
                        v-model="autoPostingSettings.badDebts"
                        :items="accountsList"
                        :search-input.sync="accountSearchBadDebts"
                        @update:search-input="
                          (val) => val && val.length > 1 && debouncedFetchAccounts(val)
                        "
                        @change="
                          (val) => {
                            if (val) {
                              autoPostingSettings.badDebts = { _id: val._id, name: val.name }
                              editModes.badDebts = false
                            }
                          }
                        "
                        return-object
                        item-text="name"
                        item-value="_id"
                        label="Bad Debts Ledger"
                        outlined
                        dense
                        class="mb-3"
                        clearable
                        hide-details
                        prepend-inner-icon="mdi-alert-circle-outline"
                        :loading="accountsLoading"
                        :filter="() => true"
                        no-data-text="Type to search accounts..."
                      ></v-autocomplete>

                      <!-- 6. Waiver Ledger -->
                      <div
                        v-if="
                          autoPostingSettings.waiverLedger &&
                          autoPostingSettings.waiverLedger.name &&
                          !editModes.waiverLedger
                        "
                        class="d-flex align-center border rounded px-3 py-2 mb-3 bg-gray-50"
                      >
                        <v-icon color="primary" class="mr-2">mdi-tag-off-outline</v-icon>
                        <div class="flex-grow-1">
                          <div class="text-xs text-gray-400">Waiver Ledger</div>
                          <div class="text-sm font-semibold">
                            {{ autoPostingSettings.waiverLedger.name }}
                          </div>
                        </div>
                        <v-btn icon small @click="editModes.waiverLedger = true" class="mr-1">
                          <v-icon small color="grey darken-1">mdi-pencil</v-icon>
                        </v-btn>
                        <v-btn
                          icon
                          small
                          @click="
                            autoPostingSettings.waiverLedger = null
                            editModes.waiverLedger = false
                          "
                        >
                          <v-icon small color="red darken-1">mdi-close</v-icon>
                        </v-btn>
                      </div>
                      <v-autocomplete
                        v-else
                        v-model="autoPostingSettings.waiverLedger"
                        :items="accountsList"
                        :search-input.sync="accountSearchWaiver"
                        @update:search-input="
                          (val) => val && val.length > 1 && debouncedFetchAccounts(val)
                        "
                        @change="
                          (val) => {
                            if (val) {
                              autoPostingSettings.waiverLedger = { _id: val._id, name: val.name }
                              editModes.waiverLedger = false
                            }
                          }
                        "
                        return-object
                        item-text="name"
                        item-value="_id"
                        label="Waiver Ledger"
                        outlined
                        dense
                        class="mb-3"
                        clearable
                        hide-details
                        prepend-inner-icon="mdi-tag-off-outline"
                        :loading="accountsLoading"
                        :filter="() => true"
                        no-data-text="Type to search accounts..."
                      ></v-autocomplete>

                      <!-- 7. EMI Forwarding -->
                      <div
                        v-if="
                          autoPostingSettings.emiForwarding &&
                          autoPostingSettings.emiForwarding.name &&
                          !editModes.emiForwarding
                        "
                        class="d-flex align-center border rounded px-3 py-2 mb-3 bg-gray-50"
                      >
                        <v-icon color="primary" class="mr-2">mdi-calendar-arrow-right</v-icon>
                        <div class="flex-grow-1">
                          <div class="text-xs text-gray-400">EMI Forwarding</div>
                          <div class="text-sm font-semibold">
                            {{ autoPostingSettings.emiForwarding.name }}
                          </div>
                        </div>
                        <v-btn icon small @click="editModes.emiForwarding = true" class="mr-1">
                          <v-icon small color="grey darken-1">mdi-pencil</v-icon>
                        </v-btn>
                        <v-btn
                          icon
                          small
                          @click="
                            autoPostingSettings.emiForwarding = null
                            editModes.emiForwarding = false
                          "
                        >
                          <v-icon small color="red darken-1">mdi-close</v-icon>
                        </v-btn>
                      </div>
                      <v-autocomplete
                        v-else
                        v-model="autoPostingSettings.emiForwarding"
                        :items="accountsList"
                        :search-input.sync="accountSearchEmiForwarding"
                        @update:search-input="
                          (val) => val && val.length > 1 && debouncedFetchAccounts(val)
                        "
                        @change="
                          (val) => {
                            if (val) {
                              autoPostingSettings.emiForwarding = { _id: val._id, name: val.name }
                              editModes.emiForwarding = false
                            }
                          }
                        "
                        return-object
                        item-text="name"
                        item-value="_id"
                        label="EMI Forwarding"
                        outlined
                        dense
                        class="mb-3"
                        clearable
                        hide-details
                        prepend-inner-icon="mdi-calendar-arrow-right"
                        :loading="accountsLoading"
                        :filter="() => true"
                        no-data-text="Type to search accounts..."
                      ></v-autocomplete>

                      <!-- 8. Collection Charges -->
                      <div
                        v-if="
                          autoPostingSettings.collectionCharges &&
                          autoPostingSettings.collectionCharges.name &&
                          !editModes.collectionCharges
                        "
                        class="d-flex align-center border rounded px-3 py-2 mb-3 bg-gray-50"
                      >
                        <v-icon color="primary" class="mr-2">mdi-cash-multiple</v-icon>
                        <div class="flex-grow-1">
                          <div class="text-xs text-gray-400">Collection Charges</div>
                          <div class="text-sm font-semibold">
                            {{ autoPostingSettings.collectionCharges.name }}
                          </div>
                        </div>
                        <v-btn icon small @click="editModes.collectionCharges = true" class="mr-1">
                          <v-icon small color="grey darken-1">mdi-pencil</v-icon>
                        </v-btn>
                        <v-btn
                          icon
                          small
                          @click="
                            autoPostingSettings.collectionCharges = null
                            editModes.collectionCharges = false
                          "
                        >
                          <v-icon small color="red darken-1">mdi-close</v-icon>
                        </v-btn>
                      </div>
                      <v-autocomplete
                        v-else
                        v-model="autoPostingSettings.collectionCharges"
                        :items="accountsList"
                        :search-input.sync="accountSearchCollectionCharges"
                        @update:search-input="
                          (val) => val && val.length > 1 && debouncedFetchAccounts(val)
                        "
                        @change="
                          (val) => {
                            if (val) {
                              autoPostingSettings.collectionCharges = {
                                _id: val._id,
                                name: val.name
                              }
                              editModes.collectionCharges = false
                            }
                          }
                        "
                        return-object
                        item-text="name"
                        item-value="_id"
                        label="Collection Charges"
                        outlined
                        dense
                        class="mb-3"
                        clearable
                        hide-details
                        prepend-inner-icon="mdi-cash-multiple"
                        :loading="accountsLoading"
                        :filter="() => true"
                        no-data-text="Type to search accounts..."
                      ></v-autocomplete>

                      <!-- Toggle -->
                      <v-divider class="mb-3 mt-2"></v-divider>
                      <div class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                        GST Payable Ledgers
                      </div>

                      <!-- 9. IGST Payable -->
                      <div
                        v-if="
                          autoPostingSettings.igstPayable &&
                          autoPostingSettings.igstPayable.name &&
                          !editModes.igstPayable
                        "
                        class="d-flex align-center border rounded px-3 py-2 mb-3 bg-gray-50"
                      >
                        <v-icon color="purple darken-1" class="mr-2">mdi-bank-transfer</v-icon>
                        <div class="flex-grow-1">
                          <div class="text-xs text-gray-400">IGST Payable Ledger</div>
                          <div class="text-sm font-semibold">
                            {{ autoPostingSettings.igstPayable.name }}
                          </div>
                        </div>
                        <v-btn icon small @click="editModes.igstPayable = true" class="mr-1">
                          <v-icon small color="grey darken-1">mdi-pencil</v-icon>
                        </v-btn>
                        <v-btn
                          icon
                          small
                          @click="
                            autoPostingSettings.igstPayable = null
                            editModes.igstPayable = false
                          "
                        >
                          <v-icon small color="red darken-1">mdi-close</v-icon>
                        </v-btn>
                      </div>
                      <v-autocomplete
                        v-else
                        v-model="autoPostingSettings.igstPayable"
                        :items="accountsList"
                        :search-input.sync="accountSearchIgstPayable"
                        @update:search-input="
                          (val) => val && val.length > 1 && debouncedFetchAccounts(val)
                        "
                        @change="
                          (val) => {
                            if (val) {
                              autoPostingSettings.igstPayable = { _id: val._id, name: val.name }
                              editModes.igstPayable = false
                            }
                          }
                        "
                        return-object
                        item-text="name"
                        item-value="_id"
                        label="IGST Payable Ledger"
                        outlined
                        dense
                        class="mb-3"
                        clearable
                        hide-details
                        prepend-inner-icon="mdi-bank-transfer"
                        :loading="accountsLoading"
                        :filter="() => true"
                        no-data-text="Type to search accounts..."
                      ></v-autocomplete>

                      <!-- 10. CGST Payable -->
                      <div
                        v-if="
                          autoPostingSettings.cgstPayable &&
                          autoPostingSettings.cgstPayable.name &&
                          !editModes.cgstPayable
                        "
                        class="d-flex align-center border rounded px-3 py-2 mb-3 bg-gray-50"
                      >
                        <v-icon color="blue darken-1" class="mr-2">mdi-bank-transfer-in</v-icon>
                        <div class="flex-grow-1">
                          <div class="text-xs text-gray-400">CGST Payable Ledger</div>
                          <div class="text-sm font-semibold">
                            {{ autoPostingSettings.cgstPayable.name }}
                          </div>
                        </div>
                        <v-btn icon small @click="editModes.cgstPayable = true" class="mr-1">
                          <v-icon small color="grey darken-1">mdi-pencil</v-icon>
                        </v-btn>
                        <v-btn
                          icon
                          small
                          @click="
                            autoPostingSettings.cgstPayable = null
                            editModes.cgstPayable = false
                          "
                        >
                          <v-icon small color="red darken-1">mdi-close</v-icon>
                        </v-btn>
                      </div>
                      <v-autocomplete
                        v-else
                        v-model="autoPostingSettings.cgstPayable"
                        :items="accountsList"
                        :search-input.sync="accountSearchCgstPayable"
                        @update:search-input="
                          (val) => val && val.length > 1 && debouncedFetchAccounts(val)
                        "
                        @change="
                          (val) => {
                            if (val) {
                              autoPostingSettings.cgstPayable = { _id: val._id, name: val.name }
                              editModes.cgstPayable = false
                            }
                          }
                        "
                        return-object
                        item-text="name"
                        item-value="_id"
                        label="CGST Payable Ledger"
                        outlined
                        dense
                        class="mb-3"
                        clearable
                        hide-details
                        prepend-inner-icon="mdi-bank-transfer-in"
                        :loading="accountsLoading"
                        :filter="() => true"
                        no-data-text="Type to search accounts..."
                      ></v-autocomplete>

                      <!-- 11. SGST Payable -->
                      <div
                        v-if="
                          autoPostingSettings.sgstPayable &&
                          autoPostingSettings.sgstPayable.name &&
                          !editModes.sgstPayable
                        "
                        class="d-flex align-center border rounded px-3 py-2 mb-3 bg-gray-50"
                      >
                        <v-icon color="teal darken-1" class="mr-2">mdi-bank-transfer-out</v-icon>
                        <div class="flex-grow-1">
                          <div class="text-xs text-gray-400">SGST Payable Ledger</div>
                          <div class="text-sm font-semibold">
                            {{ autoPostingSettings.sgstPayable.name }}
                          </div>
                        </div>
                        <v-btn icon small @click="editModes.sgstPayable = true" class="mr-1">
                          <v-icon small color="grey darken-1">mdi-pencil</v-icon>
                        </v-btn>
                        <v-btn
                          icon
                          small
                          @click="
                            autoPostingSettings.sgstPayable = null
                            editModes.sgstPayable = false
                          "
                        >
                          <v-icon small color="red darken-1">mdi-close</v-icon>
                        </v-btn>
                      </div>
                      <v-autocomplete
                        v-else
                        v-model="autoPostingSettings.sgstPayable"
                        :items="accountsList"
                        :search-input.sync="accountSearchSgstPayable"
                        @update:search-input="
                          (val) => val && val.length > 1 && debouncedFetchAccounts(val)
                        "
                        @change="
                          (val) => {
                            if (val) {
                              autoPostingSettings.sgstPayable = { _id: val._id, name: val.name }
                              editModes.sgstPayable = false
                            }
                          }
                        "
                        return-object
                        item-text="name"
                        item-value="_id"
                        label="SGST Payable Ledger"
                        outlined
                        dense
                        class="mb-3"
                        clearable
                        hide-details
                        prepend-inner-icon="mdi-bank-transfer-out"
                        :loading="accountsLoading"
                        :filter="() => true"
                        no-data-text="Type to search accounts..."
                      ></v-autocomplete>

                      <v-divider class="mb-3 mt-2"></v-divider>
                      <div class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                        GST Receivable Ledgers
                      </div>

                      <!-- 12. IGST Receivable -->
                      <div
                        v-if="
                          autoPostingSettings.igstReceivable &&
                          autoPostingSettings.igstReceivable.name &&
                          !editModes.igstReceivable
                        "
                        class="d-flex align-center border rounded px-3 py-2 mb-3 bg-gray-50"
                      >
                        <v-icon color="deep-purple darken-1" class="mr-2">mdi-bank-plus</v-icon>
                        <div class="flex-grow-1">
                          <div class="text-xs text-gray-400">IGST Receivable Ledger</div>
                          <div class="text-sm font-semibold">
                            {{ autoPostingSettings.igstReceivable.name }}
                          </div>
                        </div>
                        <v-btn icon small @click="editModes.igstReceivable = true" class="mr-1">
                          <v-icon small color="grey darken-1">mdi-pencil</v-icon>
                        </v-btn>
                        <v-btn
                          icon
                          small
                          @click="
                            autoPostingSettings.igstReceivable = null
                            editModes.igstReceivable = false
                          "
                        >
                          <v-icon small color="red darken-1">mdi-close</v-icon>
                        </v-btn>
                      </div>
                      <v-autocomplete
                        v-else
                        v-model="autoPostingSettings.igstReceivable"
                        :items="accountsList"
                        :search-input.sync="accountSearchIgstReceivable"
                        @update:search-input="
                          (val) => val && val.length > 1 && debouncedFetchAccounts(val)
                        "
                        @change="
                          (val) => {
                            if (val) {
                              autoPostingSettings.igstReceivable = { _id: val._id, name: val.name }
                              editModes.igstReceivable = false
                            }
                          }
                        "
                        return-object
                        item-text="name"
                        item-value="_id"
                        label="IGST Receivable Ledger"
                        outlined
                        dense
                        class="mb-3"
                        clearable
                        hide-details
                        prepend-inner-icon="mdi-bank-plus"
                        :loading="accountsLoading"
                        :filter="() => true"
                        no-data-text="Type to search accounts..."
                      ></v-autocomplete>

                      <!-- 13. CGST Receivable -->
                      <div
                        v-if="
                          autoPostingSettings.cgstReceivable &&
                          autoPostingSettings.cgstReceivable.name &&
                          !editModes.cgstReceivable
                        "
                        class="d-flex align-center border rounded px-3 py-2 mb-3 bg-gray-50"
                      >
                        <v-icon color="indigo darken-1" class="mr-2">mdi-bank-check</v-icon>
                        <div class="flex-grow-1">
                          <div class="text-xs text-gray-400">CGST Receivable Ledger</div>
                          <div class="text-sm font-semibold">
                            {{ autoPostingSettings.cgstReceivable.name }}
                          </div>
                        </div>
                        <v-btn icon small @click="editModes.cgstReceivable = true" class="mr-1">
                          <v-icon small color="grey darken-1">mdi-pencil</v-icon>
                        </v-btn>
                        <v-btn
                          icon
                          small
                          @click="
                            autoPostingSettings.cgstReceivable = null
                            editModes.cgstReceivable = false
                          "
                        >
                          <v-icon small color="red darken-1">mdi-close</v-icon>
                        </v-btn>
                      </div>
                      <v-autocomplete
                        v-else
                        v-model="autoPostingSettings.cgstReceivable"
                        :items="accountsList"
                        :search-input.sync="accountSearchCgstReceivable"
                        @update:search-input="
                          (val) => val && val.length > 1 && debouncedFetchAccounts(val)
                        "
                        @change="
                          (val) => {
                            if (val) {
                              autoPostingSettings.cgstReceivable = { _id: val._id, name: val.name }
                              editModes.cgstReceivable = false
                            }
                          }
                        "
                        return-object
                        item-text="name"
                        item-value="_id"
                        label="CGST Receivable Ledger"
                        outlined
                        dense
                        class="mb-3"
                        clearable
                        hide-details
                        prepend-inner-icon="mdi-bank-check"
                        :loading="accountsLoading"
                        :filter="() => true"
                        no-data-text="Type to search accounts..."
                      ></v-autocomplete>

                      <!-- 14. SGST Receivable -->
                      <div
                        v-if="
                          autoPostingSettings.sgstReceivable &&
                          autoPostingSettings.sgstReceivable.name &&
                          !editModes.sgstReceivable
                        "
                        class="d-flex align-center border rounded px-3 py-2 mb-3 bg-gray-50"
                      >
                        <v-icon color="cyan darken-2" class="mr-2">mdi-bank-minus</v-icon>
                        <div class="flex-grow-1">
                          <div class="text-xs text-gray-400">SGST Receivable Ledger</div>
                          <div class="text-sm font-semibold">
                            {{ autoPostingSettings.sgstReceivable.name }}
                          </div>
                        </div>
                        <v-btn icon small @click="editModes.sgstReceivable = true" class="mr-1">
                          <v-icon small color="grey darken-1">mdi-pencil</v-icon>
                        </v-btn>
                        <v-btn
                          icon
                          small
                          @click="
                            autoPostingSettings.sgstReceivable = null
                            editModes.sgstReceivable = false
                          "
                        >
                          <v-icon small color="red darken-1">mdi-close</v-icon>
                        </v-btn>
                      </div>
                      <v-autocomplete
                        v-else
                        v-model="autoPostingSettings.sgstReceivable"
                        :items="accountsList"
                        :search-input.sync="accountSearchSgstReceivable"
                        @update:search-input="
                          (val) => val && val.length > 1 && debouncedFetchAccounts(val)
                        "
                        @change="
                          (val) => {
                            if (val) {
                              autoPostingSettings.sgstReceivable = { _id: val._id, name: val.name }
                              editModes.sgstReceivable = false
                            }
                          }
                        "
                        return-object
                        item-text="name"
                        item-value="_id"
                        label="SGST Receivable Ledger"
                        outlined
                        dense
                        class="mb-3"
                        clearable
                        hide-details
                        prepend-inner-icon="mdi-bank-minus"
                        :loading="accountsLoading"
                        :filter="() => true"
                        no-data-text="Type to search accounts..."
                      ></v-autocomplete>

                      <v-divider class="mb-3"></v-divider>
                      <div class="flex items-center">
                        <v-switch
                          v-model="autoPostingSettings.enablePrincipalBadDebtLedger"
                          color="primary"
                          hide-details
                          class="mt-0 pt-0"
                          dense
                        ></v-switch>
                        <div class="ml-3">
                          <p class="text-sm font-semibold mb-0">Enable Principal Bad Debt Ledger</p>
                          <p class="text-xs text-gray-500 mb-0">
                            Post principal write-offs to a separate bad debt ledger
                          </p>
                        </div>
                      </div>
                    </v-card>
                  </v-col>
                </v-row>

                <div class="flex justify-end gap-4 mt-6">
                  <v-btn elevation="2" class="normal-case" small text @click="onCancel">
                    Cancel
                  </v-btn>
                  <v-btn
                    depressed
                    @click="Array.isArray(useCaseLabel) ? onSubmit() : createExtendUseCase()"
                    color="primary"
                    elevation="2"
                    small
                    class="white--text normal-case"
                  >
                    <v-icon left>mdi-content-save</v-icon>
                    Save Auto Posting
                  </v-btn>
                </div>
              </div>
            </v-tab-item>

            <!-- 3. Tax Settings Sub-tab -->
            <v-tab-item>
              <div class="p-4 pt-2">
                <TaxSettings v-model="taxSettings" />
                <div class="flex justify-end gap-4 mt-6">
                  <v-btn elevation="2" class="normal-case" small text @click="onCancel">
                    Cancel
                  </v-btn>
                  <v-btn
                    depressed
                    @click="Array.isArray(useCaseLabel) ? onSubmit() : createExtendUseCase()"
                    color="primary"
                    elevation="2"
                    small
                    class="white--text normal-case"
                  >
                    <v-icon left>mdi-content-save</v-icon>
                    Save Tax Settings
                  </v-btn>
                </div>
              </div>
            </v-tab-item>

            <!-- 4. Charges / VAS Posting Sub-tab -->
            <v-tab-item>
              <div class="p-4 pt-2">
                <VASPostingSettings v-model="vasPostingSettings" />
                <div class="flex justify-end gap-4 mt-6">
                  <v-btn elevation="2" class="normal-case" small text @click="onCancel">
                    Cancel
                  </v-btn>
                  <v-btn
                    depressed
                    @click="Array.isArray(useCaseLabel) ? onSubmit() : createExtendUseCase()"
                    color="primary"
                    elevation="2"
                    small
                    class="white--text normal-case"
                  >
                    <v-icon left>mdi-content-save</v-icon>
                    Save VAS Posting
                  </v-btn>
                </div>
              </div>
            </v-tab-item>
          </v-tabs-items>
        </div>
      </v-tab-item>

      <div class="flex justify-end gap-6 m-4" v-if="!isUploadCase && mainTab !== 2">
        <div class="flex justify-end gap-4">
          <v-btn elevation="2" class="normal-case" small text @click="onCancel"> Cancel </v-btn>
          <!-- @click="editSubCase?.key === 'COLLECTION' ? onSubmit() : createExtendUseCase()" -->
          <v-btn
            depressed
            @click="Array.isArray(useCaseLabel) ? onSubmit() : createExtendUseCase()"
            color="primary"
            elevation="2"
            small
            class="white--text normal-case"
          >
            <v-icon left>mdi-content-save</v-icon>
            Save
          </v-btn>
        </div>
      </div>
    </v-tabs-items>

    <AlertPopUp
      :showConfirmationDialog="showConfirmationClone"
      :confirmationTitle="'Confirmation for existing questions'"
      :confirmationMessage="'Questions already exist in this case outcome type, you want to keep the previous questions or replace with new questions'"
      :confirmationActionText="'Replace with new questions'"
      :cancelActionText="'Keep previous questions'"
      :confirmationActionColor="'green'"
      :performAction="cloneNewQuestions"
      :cancel="cloneWithExistingQuestions"
    />
  </div>
</template>

<script>
// import Questionnaire from './OrgConfigComponents/Questionnaire'
import CaseOutcomeStatus from './OrgConfigComponents/CaseOutcomeStatus'
import AlertPopUp from '@/components/AlertPopUp'
import { mergeOptions } from '@/utils/common'
import DataMapping from './OrgConfigComponents/DataMapping.vue'
import { convertToTitleCase } from '@/utils/common'
import { dynamicRequestPath } from '@/utils/workforce/constants'
import IconSelector from '@/components/common/IconSelector.vue'
import { axiosUrl } from '@/utils/workforceApi'
import GeneralSettings from './OrgConfigComponents/GeneralSettings.vue'
import TaxSettings from './OrgConfigComponents/TaxSettings.vue'
import VASPostingSettings from './OrgConfigComponents/VASPostingSettings.vue'

export default {
  name: 'OrgMapping',
  components: {
    CaseOutcomeStatus,
    AlertPopUp,
    DataMapping,
    IconSelector,
    GeneralSettings,
    TaxSettings,
    VASPostingSettings
  },
  data() {
    return {
      userOrg: this.$storage.getUniversal('user'),
      caseType: {
        text: 'Sub-case Name',
        key: 'caseType',
        value: '',
        icon: '',
        rules: [(v) => !!v || 'Subcase Name is required']
      },
      tabs: 0,

      ledgerGroups: [],
      accountSubTab: 0,
      generalSettings: {},
      taxSettings: [],
      vasPostingSettings: [],
      autoPostingSettings: {
        loanGroup: null,
        interest: null,
        badDebts: null,
        waiverLedger: null,
        handLoanLpInterest: null,
        lpInterest: null,
        emiForwarding: null,
        collectionCharges: null,
        enablePrincipalBadDebtLedger: false,
        igstPayable: null,
        cgstPayable: null,
        sgstPayable: null,
        igstReceivable: null,
        cgstReceivable: null,
        sgstReceivable: null
      },
      accountsList: [],
      accountsLoading: false,
      // Search sync vars for autocomplete fields
      accountSearchIgstPayable: '',
      accountSearchCgstPayable: '',
      accountSearchSgstPayable: '',
      accountSearchIgstReceivable: '',
      accountSearchCgstReceivable: '',
      accountSearchSgstReceivable: '',
      editModes: {
        loanGroup: false,
        interest: false,
        lpInterest: false,
        handLoanLpInterest: false,
        badDebts: false,
        waiverLedger: false,
        emiForwarding: false,
        collectionCharges: false,
        igstPayable: false,
        cgstPayable: false,
        sgstPayable: false,
        igstReceivable: false,
        cgstReceivable: false,
        sgstReceivable: false
      },
      dataMappingTab: 0,
      orgKeys: [],
      // Static reference tabs - should never change
      tableTabs: [
        { text: 'Cases', key: 'caseTable', value: 'Case', icon: 'mdi-briefcase', visible: true },
        {
          text: 'Visits',
          key: 'visitTable',
          value: 'Visit',
          icon: 'mdi-google-maps',
          visible: true
        },
        {
          text: 'Case Analytics',
          key: 'caseAnalyticsTable',
          value: 'Case Analytics',
          icon: 'mdi-chart-line',
          visible: true
        },
        {
          text: 'Call History',
          key: 'callHistoryTable',
          value: 'Call History',
          icon: 'mdi-phone-log',
          visible: true
        },
        {
          text: 'Customers',
          key: 'customerTable',
          value: 'Customers',
          icon: 'mdi-account-multiple',
          visible: true
        },
        {
          text: 'Audit',
          key: 'auditTable',
          value: 'Audit',
          icon: 'mdi-history',
          visible: true
        }
      ],
      staticFields: {
        crn: '',
        customerFullName: '',
        customerMobile: '',
        customerEmail: '',
        crnRules: [(v) => !!v || 'CRN No is required'],
        customerFullNameRules: [(v) => !!v || 'Customer Name is required']
      },
      addressTypes: [
        'CURRENT_ADDRESS',
        'PERMANENT_ADDRESS',
        'RESIDENTIAL_ADDRESS',
        'OFFICE_ADDRESS',
        'BILLING_ADDRESS',
        'MAILING_ADDRESS'
      ],
      addressFields: {
        addressLine1: { key: '', text: 'Address Line 1' },
        addressLine2: { key: '', text: 'Address Line 2' },
        landMark: { key: '', text: 'Land Mark' },
        city: { key: '', text: 'City' },
        state: { key: '', text: 'State' },
        country: { key: '', text: 'Country' },
        pinCode: { key: '', text: 'Pincode' }
      },
      addresses: [
        {
          typeOfAddress: 'CURRENT_ADDRESS',
          default: false,
          addressLine1: { key: '', displayName: '' },
          addressLine2: { key: '', displayName: '' },
          landMark: { key: '', displayName: '' },
          city: { key: '', displayName: '' },
          state: { key: '', displayName: '' },
          country: { key: '', displayName: '' },
          pinCode: { key: '', displayName: '' }
        }
      ],
      loading: false,
      caseOutcomeStatus: '',
      enableOutcomeColumn: true,
      caseStatusName: '',
      caseStatusesInput: ['Open', 'Closed', 'In-Progress'],
      fieldPanel: 0,
      statusPanel: 0,
      panel: 0,
      extraColumns: [],
      branches: [],
      selectedBranch: '',
      valid: true,
      subCaseQuestions: {},
      customerFeedback: {},
      alertMessage: '',
      showConfirmationClone: false,
      clonedValue: null,
      clonedKey: null,
      cloneTarget: null,
      entityName: 'Entity',
      // Dynamic tabs - can be modified based on user input
      tableTabKeys: [],
      entityEnabled: false,
      dataCategorizations: [],
      addedDataCategories: [],
      enableBranchMapping: false,
      enableEntityMapping: false,
      enableDataCategoryMapping: false,
      enableLoanTypeMapping: false,
      selectedLoanTypes: [],
      loanTypeOptions: [
        'UnSecured Loan',
        'Top-Up Loan',
        'Secured Loan',
        'Green Channel',
        'Direct Channel',
        'Unsecured Part',
        'Mutual Fund(Secured)',
        'Invoice Discounting',
        'Credit Line',
        'Consumer Durable',
        'Insurance (Secured)',
        'Solar Loan',
        'Salaried Credit'
      ],
      enableLoanCategoryMapping: false,
      selectedLoanCategories: [],
      loanCategoryOptions: ['Personal', 'Home', 'Auto', 'Gold', 'Business', 'Education', 'Other'],
      enableLeadSourceMapping: false,
      selectedLeadSources: [],
      leadSourceOptions: [
        'DSA',
        'Consultant',
        'Dealer',
        'Customer',
        'DST',
        'Website',
        'Referral',
        'Tele Calling',
        'Others'
      ],
      enableCrossLinking: false,
      crossLinkedSubCaseId: null,

      allSubCases: [],
      mainTab: 0,
      categories: [
        // {
        //   categoryName: '',
        //   options: [],
        //   optionsList: [],
        //   assignedTemplates: {},
        //   caseOutcomeFormId: {},
        //   customerFormId: {}
        // }
      ],
      baseFieldMappings: {},
      apiIcons: [],
      iconSelectorDialog: false,
      currentEditingItem: null,
      currentEditingType: '',
      iconsLoaded: false,
      loadingIcons: false,
      branchesLoaded: false,
      loadingBranches: false,
      iconSelectorLoading: false,
      iconButtonLoading: {}
    }
  },
  props: {
    useCase: String,
    enabledSubCases: {
      type: Array,
      default: () => []
    },
    useCaseLabel: {
      type: [Object, Array],
      default: () => ({})
    },
    baseMapping: Object,
    editSubCase: Object,
    cloneSubCase: Object,
    isNewSubCase: Boolean,
    isUploadCase: Boolean,
    allowedQuestionsTypes: Array,
    allowedQuestionsTypesCustomerFeedback: Array,
    isWhatsAppEnabled: Boolean,
    showMappingDetails: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    catOptionTabs() {
      return mergeOptions(this.categories) || []
    },
    mendatoryFields() {
      return this.orgKeys
        .filter((key) =>
          [
            'pos',
            'emiAmount',
            'tenure',
            'typeOfLoan',
            'productInfo',
            'taskTitle',
            'taskDescription',
            'bkt'
          ].includes(key.key)
        )
        .map((key) => ({
          text: key.text,
          value: key.value || key.text,
          key: key.key
        }))
    },
    dynamicOrgKeys() {
      const keys = []
      if (this.useCaseLabel || this.baseMapping) {
        const combinedLabels = {
          ...this.baseMapping,
          ...this.useCaseLabel
        }
        Object.entries(combinedLabels).forEach(([key, value]) => {
          if (key === 'status' || key === 'outcome') return
          keys.push({
            text: value.text || key,
            key: key,
            value: value.displayName || '',
            visible: value.visible !== undefined ? value.visible : true,
            required: value.required !== undefined ? value.required : false,
            prefix: value.prefix || undefined,
            suffix: value.suffix || undefined,
            formVisibility: value.formVisibility !== undefined ? value.formVisibility : false,
            tableVisibility: value.tableVisibility !== undefined ? value.tableVisibility : false
            // ...(key === 'caseNo' && { rules: [(v) => !!v || 'Case No is required'] })
          })
        })
      }

      return keys
    },
    allLoanTypesSelected() {
      return this.selectedLoanTypes.length === this.loanTypeOptions.length
    },
    someLoanTypesSelected() {
      return this.selectedLoanTypes.length > 0 && !this.allLoanTypesSelected
    },
    categorizedSubCases() {
      // Exclude current sub-case from linking to itself
      return this.allSubCases
        .filter((s) => s._id !== this.editSubCase?._id)
        .sort((a, b) => a.useCase.localeCompare(b.useCase))
    },
    allLoansSelectedIcon() {
      if (this.allLoanTypesSelected) return 'mdi-close-box'
      if (this.someLoanTypesSelected) return 'mdi-minus-box'
      return 'mdi-checkbox-blank-outline'
    },
    allLeadSourcesSelected() {
      return this.selectedLeadSources.length === this.leadSourceOptions.length
    },
    someLeadSourcesSelected() {
      return this.selectedLeadSources.length > 0 && !this.allLeadSourcesSelected
    },
    allLeadSourcesSelectedIcon() {
      if (this.allLeadSourcesSelected) return 'mdi-close-box'
      if (this.someLeadSourcesSelected) return 'mdi-minus-box'
      return 'mdi-checkbox-blank-outline'
    },
    allLoanCategoriesSelected() {
      return this.selectedLoanCategories.length === this.loanCategoryOptions.length
    },
    someLoanCategoriesSelected() {
      return this.selectedLoanCategories.length > 0 && !this.allLoanCategoriesSelected
    },
    allLoanCategoriesSelectedIcon() {
      if (this.allLoanCategoriesSelected) return 'mdi-close-box'
      if (this.someLoanCategoriesSelected) return 'mdi-minus-box'
      return 'mdi-checkbox-blank-outline'
    }
  },
  methods: {
    titleCase(str) {
      return convertToTitleCase(str)
    },

    buildStaticFieldsAndAddresses() {
      const formatKey = (displayName) => {
        return (displayName || '').trim().replace(/\s+/g, '_').toLowerCase()
      }

      let staticFieldMappings = {}

      // Add static fields
      staticFieldMappings['crn'] = {
        key: formatKey(this.staticFields?.crn),
        displayName: this.staticFields?.crn,
        visibility: true,
        formVisibility: false,
        tableVisibility: false
      }
      staticFieldMappings['customerFullName'] = {
        key: formatKey(this.staticFields?.customerFullName),
        displayName: this.staticFields?.customerFullName,
        visibility: true,
        formVisibility: false,
        tableVisibility: false
      }
      staticFieldMappings['customerMobile'] = {
        key: formatKey(this.staticFields?.customerMobile),
        displayName: this.staticFields?.customerMobile,
        visibility: true,
        formVisibility: false,
        tableVisibility: false
      }
      staticFieldMappings['customerEmail'] = {
        key: formatKey(this.staticFields?.customerEmail),
        displayName: this.staticFields?.customerEmail,
        visibility: true,
        formVisibility: false,
        tableVisibility: false
      }

      // Add addresses if present
      if (this.addresses[0]?.addressLine1?.displayName) {
        staticFieldMappings.addresses = this.addresses?.map((address) => {
          if (!address.addressLine1 || !address.addressLine1.displayName) {
            throw new Error(`Address Line 1 is required for ${address.typeOfAddress}.`)
          }

          let addressMapping = { typeOfAddress: address.typeOfAddress }
          Object.keys(this.addressFields)?.forEach((fieldKey) => {
            const field = address[fieldKey]
            const transformedKey = formatKey(field.displayName)
            addressMapping[fieldKey] = {
              key: transformedKey || fieldKey,
              displayName: field.displayName
            }
          })
          return addressMapping
        })

        const defaultAddress = this.addresses.find((address) => address.default)
        staticFieldMappings.defaultAddress = defaultAddress ? defaultAddress.typeOfAddress : null
      }

      return staticFieldMappings
    },
    removeExtraColumn(index) {
      this.extraColumns.splice(index, 1)
    },
    getFilteredItems(index) {
      const search = this.extraColumns[index].search || this.extraColumns[index].text
      if (search) {
        const baseItems = ['STRING', 'NUMBER', 'DATE', 'CURRENCY', 'SELECT']
        return baseItems.map((type) => ({
          text: `${search} - ${type}`,
          value: `${search} - ${type}`
        }))
      }
      return []
    },
    getItemType(item) {
      const itemType = item?.split('-')
      const type = itemType[1]?.trim()
      // console.log(itemType[1]?.trim());
      return type
    },

    updateColumnValue(index, value) {
      this.extraColumns[index].value = value
    },
    addExtraColumn() {
      const newColumnIndex = this.extraColumns.length
      const newColumn = {
        text: `Column ${newColumnIndex + 1}`,
        key: `column_${newColumnIndex + 1}`,
        value: '',
        search: '',
        formVisibility: false,
        tableVisibility: false,
        required: false
      }

      this.extraColumns = [...this.extraColumns, newColumn]
    },

    removeCategory(index) {
      this.categories.splice(index, 1)
    },
    removeAddress(index) {
      this.checkForDuplicateAddressLine1()
      this.addresses.splice(index, 1)
    },
    addAddress() {
      const newAddress = {
        typeOfAddress: '',
        addressLine1: { key: '', displayName: '' },
        default: false,
        addressLine2: { key: '', displayName: '' },
        landMark: { key: '', displayName: '' },
        city: { key: '', displayName: '' },
        state: { key: '', displayName: '' },
        country: { key: '', displayName: '' },
        pinCode: { key: '', displayName: '' }
      }
      this.addresses.push(newAddress)
      this.checkForDuplicateAddressLine1()
    },
    setDefault(index) {
      this.addresses.forEach((address, i) => {
        if (i !== index) {
          address.default = false
        }
      })
    },

    getFilteredAddressTypes(currentIndex) {
      const selectedTypes = this.addresses
        .filter((_, index) => index !== currentIndex)
        .map((address) => address.typeOfAddress)

      return this.addressTypes.filter((type) => !selectedTypes.includes(type))
    },
    async createExtendUseCase() {
      try {
        let fieldMappings = {}
        let additionalColumns = {}
        let tableTabMapping = {}
        let hasLoanManagementKeys = false
        const dynamicRequestPath = {
          collection_management: {
            path: 'collection-management-field-mapping',
            key: 'collectionManagementFieldMapping'
          },
          loan_management: {
            path: 'loan-management-field-mapping',
            key: 'loanManagementFieldMapping'
          }
        }

        const formatKey = (displayName) => {
          return (displayName || '').trim().replace(/\s+/g, '_').toLowerCase()
        }

        this.orgKeys.forEach((orgKey) => {
          if (this.baseMapping[orgKey.key]) {
            this.baseMapping[orgKey.key].displayName = orgKey.value
            this.baseMapping[orgKey.key].key = formatKey(orgKey.value)
            this.baseMapping[orgKey.key].formVisibility =
              orgKey.formVisibility !== undefined ? orgKey.formVisibility : false
            this.baseMapping[orgKey.key].tableVisibility =
              orgKey.tableVisibility !== undefined ? orgKey.tableVisibility : false
            this.baseMapping[orgKey.key].required =
              orgKey.required !== undefined ? orgKey.required : false
            if (orgKey.key === 'caseNo') {
              this.baseMapping[orgKey.key].prefix = orgKey.selectedPrefix
              this.baseMapping[orgKey.key].suffix = orgKey.selectedSuffix
              this.baseMapping[orgKey.key].isAutoGenerated = orgKey.isAutoGenerated
            }
          } else if (this.useCaseLabel[orgKey.key]) {
            this.useCaseLabel[orgKey.key].displayName = orgKey.value
            this.useCaseLabel[orgKey.key].formVisibility =
              orgKey.formVisibility !== undefined ? orgKey.formVisibility : false
            this.useCaseLabel[orgKey.key].tableVisibility =
              orgKey.tableVisibility !== undefined ? orgKey.tableVisibility : false
            this.useCaseLabel[orgKey.key].required =
              orgKey.required !== undefined ? orgKey.required : false
            hasLoanManagementKeys = true
          } else {
            fieldMappings[orgKey.key] = {
              key: formatKey(orgKey.value),
              displayName: orgKey.value,
              visible: orgKey.visible !== undefined ? orgKey.visible : true,
              formVisibility: orgKey.formVisibility !== undefined ? orgKey.formVisibility : false,
              tableVisibility:
                orgKey.tableVisibility !== undefined ? orgKey.tableVisibility : false,
              required: orgKey.required !== undefined ? orgKey.required : false
            }
          }
        })

        // Add static fields and addresses using helper function
        try {
          const staticFieldMappings = this.buildStaticFieldsAndAddresses()
          fieldMappings = { ...fieldMappings, ...staticFieldMappings }
        } catch (error) {
          this.$toast.error(error.message)
          return
        }

        if (this.baseMapping['status']) {
          this.baseMapping['status'].displayName = this.caseStatusName
          this.baseMapping['status'].key = formatKey(this.caseStatusName)
          this.baseMapping['status'].visibility = true
        }

        if (this.baseMapping['outcome']) {
          this.baseMapping['outcome'].displayName = this.caseOutcomeStatus
          this.baseMapping['outcome'].key = formatKey(this.caseOutcomeStatus)
          this.baseMapping['outcome'].visibility = this.enableOutcomeColumn
          this.baseMapping['outcome'].tableVisibility = this.enableOutcomeColumn
          this.baseMapping['outcome'].formVisibility = this.enableOutcomeColumn
        }

        this.tableTabKeys.forEach((tk) => {
          const newKey = formatKey(tk.value)
          tableTabMapping[tk.key] = {
            text: tk.text,
            key: newKey,
            displayName: tk.value?.trim(),
            icon: tk.icon || 'mdi-apps',
            visible: tk.visible !== undefined ? tk.visible : true
          }
        })

        this.extraColumns.forEach((el) => {
          const display = el.value?.split('-')
          const newKey = display[0]?.trim().split(' ').join('_').toLowerCase()
          const type = display[1]?.trim()
          additionalColumns[newKey] = {
            key: newKey,
            displayName: display[0].trim(),
            fieldType: type,
            options:
              type === 'SELECT' && el.options
                ? el.options.split(',').map((o) => o.trim())
                : undefined,
            formVisibility: el.formVisibility !== false,
            tableVisibility: el.tableVisibility !== false,
            required: el.required || false
          }
        })

        let loanManagementFieldMappingId = null

        // Handle loan management API update if editing
        if (dynamicRequestPath[this.useCase]?.key) {
          if (
            this.$props.editSubCase &&
            this.$props.editSubCase[dynamicRequestPath[this.useCase]?.key]
          ) {
            const loanManagementPayload = {
              ...this.useCaseLabel
            }

            // Update existing loan management mapping
            const loanMappingResponse = await this.$axios.put(
              `/orgSetup/${dynamicRequestPath[this.useCase]?.path}/${
                this.$props.editSubCase[dynamicRequestPath[this.useCase]?.key]?._id
              }`,
              loanManagementPayload
            )

            loanManagementFieldMappingId =
              this.$props.editSubCase[dynamicRequestPath[this.useCase]?.key]?._id
          } else if (hasLoanManagementKeys) {
            // Create new loan management mapping
            const loanManagementPayload = {
              ...this.useCaseLabel
            }

            const loanMappingResponse = await this.$axios.post(
              `/orgSetup/${dynamicRequestPath[this.useCase]?.path}`,
              loanManagementPayload
            )

            loanManagementFieldMappingId = loanMappingResponse.data?.data?._id
          }
        }

        const userDefinedCaseStatus = this.categories
        const dataCategories = this.addedDataCategories.map((category) => {
          return { name: category }
        })

        const entityKey = this.entityName?.trim()?.split(' ')?.join('_').toLowerCase()

        const caseStatuses = this.caseStatusesInput.map((label) => {
          const value = label
            .trim()
            .toUpperCase()
            .replace(/[^A-Z0-9]/g, '_')
          return { label: label.trim(), value }
        })

        let extendUseCasePayload = {
          useCase: this.$props.useCase.toLowerCase(),
          branch: this.enableBranchMapping ? this.selectedBranch : undefined,
          name: this.caseType.value,
          icon: this.caseType.icon || '',
          customerFieldMappings: fieldMappings,
          additionalColumns,
          tableTabMapping,
          entity: {
            displayName: this.entityName,
            key: entityKey,
            enabled: this.entityEnabled
          },
          baseFieldMappings: this.baseMapping,
          questionnaire: this.subCaseQuestions || {},
          userFeedbackQuestionnaire: this.customerFeedback || {},
          userDefinedCaseStatus,
          dataCategories,
          caseStatuses,
          loanTypes: this.loanTypeOptions.map((option) => ({
            type: option,
            enabled: this.selectedLoanTypes.includes(option)
          })),
          loanCategories: [
            ...new Set([...this.loanCategoryOptions, ...this.selectedLoanCategories])
          ].map((option) => ({
            type: option,
            enabled: this.selectedLoanCategories.includes(option)
          })),
          leadSource: this.leadSourceOptions.map((option) => ({
            type: option,
            enabled: this.selectedLeadSources.includes(option)
          })),
          crossLinkedSubCaseId: this.enableCrossLinking ? this.crossLinkedSubCaseId : null,
          autoPostingSettings: this.autoPostingSettings
        }
        // console.log({ extendUseCasePayload })
        if (loanManagementFieldMappingId) {
          extendUseCasePayload[dynamicRequestPath[this.useCase].key] = loanManagementFieldMappingId
        }
        let extendUseCaseResponse

        // Update or create extend usecase based on whether we're editing
        if (this.$props.editSubCase && this.$props.editSubCase._id) {
          extendUseCaseResponse = await this.$axios.put(
            `/orgSetup/extend-usecase/${this.$props.editSubCase._id}`,
            extendUseCasePayload
          )
          this.$toast.success('Use case updated successfully')
        } else {
          extendUseCaseResponse = await this.$axios.post(
            '/orgSetup/extend-usecase',
            extendUseCasePayload
          )
          this.$toast.success('Use case saved as draft successfully')
        }

        const extendUseCaseId =
          this.$props.editSubCase?._id || extendUseCaseResponse.data?.data?._id
        if (extendUseCaseId) {
          await this.$axios.post(axiosUrl.accounts.upsertAccountSettings, {
            extendUseCaseId,
            generalSettings: this.generalSettings,
            autoPostingSettings: this.autoPostingSettings,
            taxSettings: this.taxSettings,
            vasPostingSettings: this.vasPostingSettings
          })
        }

        this.$emit('orgMapKeys', extendUseCaseResponse.data.data)
      } catch (error) {
        console.error('Error in createExtendUseCase:', error)
        this.$toast.error(
          error.response?.data?.message || 'An error occurred while saving the draft'
        )
      }
    },

    async onSubmit() {
      let fieldMappings = {}
      let additionalColumns = {}
      let tableTabMapping = {}
      const isFormValid = this.$refs.form ? this.$refs.form.validate() : true
      if (!isFormValid) {
        this.$toast.error('Validation failed. Please fill all the required field in the form.')
        return
      }

      const formatKey = (displayName) => {
        return (displayName || '').trim().replace(/\s+/g, '_').toLowerCase()
      }

      // Add static fields and addresses using helper function
      let isValid = true
      try {
        const staticFieldMappings = this.buildStaticFieldsAndAddresses()
        fieldMappings = { ...fieldMappings, ...staticFieldMappings }
      } catch (error) {
        this.$toast.error(error.message)
        isValid = false
      }

      if (!isValid) {
        return
      }

      const userDefinedCaseStatus = this.categories

      this.orgKeys.forEach((el) => {
        fieldMappings[el.key] = {
          key: formatKey(el.value),
          displayName: el.value,
          visibility: el.visible,
          formVisibility: el.formVisibility !== undefined ? el.formVisibility : false,
          tableVisibility: el.tableVisibility !== undefined ? el.tableVisibility : false,
          required: el.required,
          ...(el.key === 'caseNo' && {
            prefix: el.selectedPrefix,
            suffix: el.selectedSuffix,
            isAutoGenerated: el.isAutoGenerated
          })
        }
      })

      if (this.baseMapping) {
        if (this.baseMapping['status']) {
          this.baseMapping['status'].displayName = this.caseStatusName
          this.baseMapping['status'].key = formatKey(this.caseStatusName)
          this.baseMapping['status'].visibility = true
        }
        if (this.baseMapping['outcome']) {
          this.baseMapping['outcome'].displayName = this.caseOutcomeStatus
          this.baseMapping['outcome'].key = formatKey(this.caseOutcomeStatus)
          this.baseMapping['outcome'].visibility = this.enableOutcomeColumn
          this.baseMapping['outcome'].tableVisibility = this.enableOutcomeColumn
          this.baseMapping['outcome'].formVisibility = this.enableOutcomeColumn
        }
      }

      this.tableTabKeys.forEach((tk) => {
        const newKey = formatKey(tk.value)
        tableTabMapping[tk.key] = {
          text: tk.text,
          key: newKey,
          displayName: tk.value?.trim(),
          icon: tk.icon || 'mdi-apps',
          visible: tk.visible !== undefined ? tk.visible : true
        }
      })

      const dataCategories = this.addedDataCategories.map((category) => {
        return { name: category }
      })

      this.extraColumns.forEach((el) => {
        const display = el.value?.split('-')
        const newKey = display[0]?.trim().split(' ').join('_').toLowerCase()
        const type = display[1].trim()
        additionalColumns[newKey] = {
          key: newKey,
          displayName: display[0].trim(),
          fieldType: type,
          options:
            type === 'SELECT' && el.options
              ? el.options.split(',').map((o) => o.trim())
              : undefined,
          formVisibility: el.formVisibility !== false,
          tableVisibility: el.tableVisibility !== false,
          required: el.required || false
        }
      })
      // console.log(this.extraColumns);
      // console.log(additionalColumns);

      const existingSubCaseNames = this.$props.enabledSubCases.flatMap((sub) =>
        sub.subCase.map((s) => s.name.toLowerCase())
      )
      const isDuplicateCaseName = existingSubCaseNames.includes(this.caseType?.value?.toLowerCase())

      if (isDuplicateCaseName) {
        this.$toast.error('Case type name must be unique. A case with this name already exists.')
        return
      }

      const existingSubCase = this.$props.enabledSubCases.flatMap((sub) => sub.subCase)

      let enabledSubCases
      const entityKey = this.entityName?.trim()?.split(' ')?.join('_').toLowerCase()
      const caseStatuses = this.caseStatusesInput.map((label) => {
        const value = label
          .trim()
          .toUpperCase()
          .replace(/[^A-Z0-9]/g, '_')
        return { label: label.trim(), value }
      })

      if (this.$props.cloneSubCase?.cloneIndex) {
        if (this.checkDuplicateCaseType()) return
        enabledSubCases = [
          {
            useCase: this.$props.useCase.toLowerCase(),
            subCase: [
              ...existingSubCase,
              {
                branch: this.enableBranchMapping ? this.selectedBranch : undefined,
                name: this.caseType.value,
                icon: this.caseType.icon || '',
                entity: {
                  displayName: this.entityName,
                  key: entityKey,
                  enabled: this.entityEnabled
                },
                fieldMappings,
                baseFieldMappings: this.baseMapping,
                additionalColumns,
                tableTabMapping,
                userDefinedCaseStatus,
                questionnaire: this.subCaseQuestions || {},
                userFeedbackQuestionnaire: this.customerFeedback || {},
                dataCategories,
                caseStatuses,
                loanTypes: this.loanTypeOptions.map((option) => ({
                  type: option,
                  enabled: this.selectedLoanTypes.includes(option)
                })),
                loanCategories: [
                  ...new Set([...this.loanCategoryOptions, ...this.selectedLoanCategories])
                ].map((option) => ({
                  type: option,
                  enabled: this.selectedLoanCategories.includes(option)
                })),
                leadSource: this.leadSourceOptions.map((option) => ({
                  type: option,
                  enabled: this.selectedLeadSources.includes(option)
                })),
                autoPostingSettings: this.autoPostingSettings
              }
            ]
          }
        ]
      }

      if (this.$props.editSubCase?.indx !== undefined) {
        existingSubCase[this.$props.editSubCase.indx] = {
          branch: this.selectedBranch || undefined,
          name: this.caseType.value,
          icon: this.caseType.icon || '',
          entity: {
            displayName: this.entityName,
            key: entityKey,
            enabled: this.entityEnabled
          },
          fieldMappings,
          baseFieldMappings: this.baseMapping,
          additionalColumns,
          tableTabMapping,
          userDefinedCaseStatus,
          questionnaire: this.subCaseQuestions || {},
          userFeedbackQuestionnaire: this.customerFeedback || {},
          dataCategories,
          caseStatuses,
          loanTypes: this.loanTypeOptions.map((option) => ({
            type: option,
            enabled: this.selectedLoanTypes.includes(option)
          })),
          loanCategories: [
            ...new Set([...this.loanCategoryOptions, ...this.selectedLoanCategories])
          ].map((option) => ({
            type: option,
            enabled: this.selectedLoanCategories.includes(option)
          })),
          leadSource: this.leadSourceOptions.map((option) => ({
            type: option,
            enabled: this.selectedLeadSources.includes(option)
          })),
          autoPostingSettings: this.autoPostingSettings
        }

        enabledSubCases = [
          {
            useCase: this.$props.useCase.toLowerCase(),
            subCase: existingSubCase
          }
        ]
      } else {
        if (this.checkDuplicateCaseType()) return
        enabledSubCases = [
          {
            useCase: this.$props.useCase.toLowerCase(),
            subCase: [
              ...existingSubCase,
              {
                branch: this.selectedBranch || undefined,
                name: this.caseType.value,
                icon: this.caseType.icon || '',
                entity: {
                  displayName: this.entityName,
                  key: entityKey,
                  enabled: this.entityEnabled
                },
                fieldMappings,
                baseFieldMappings: this.baseMapping,
                additionalColumns,
                tableTabMapping,
                userDefinedCaseStatus,
                questionnaire: this.subCaseQuestions || {},
                userFeedbackQuestionnaire: this.customerFeedback || {},
                dataCategories,
                caseStatuses,
                loanTypes: this.loanTypeOptions.map((option) => ({
                  type: option,
                  enabled: this.selectedLoanTypes.includes(option)
                })),
                loanCategories: [
                  ...new Set([...this.loanCategoryOptions, ...this.selectedLoanCategories])
                ].map((option) => ({
                  type: option,
                  enabled: this.selectedLoanCategories.includes(option)
                })),
                leadSource: this.leadSourceOptions.map((option) => ({
                  type: option,
                  enabled: this.selectedLeadSources.includes(option)
                })),
                autoPostingSettings: this.autoPostingSettings
              }
            ]
          }
        ]
      }

      // console.log(enabledSubCases, ' enabled')
      this.$emit('orgSubCaseMapKeys', enabledSubCases)

      // Also save account settings (tax, auto posting, VAS) if we are editing an existing sub-case
      const extendUseCaseId = this.$props.editSubCase?._id
      if (extendUseCaseId) {
        try {
          await this.$axios.post(axiosUrl.accounts.upsertAccountSettings, {
            extendUseCaseId,
            generalSettings: this.generalSettings,
            autoPostingSettings: this.autoPostingSettings,
            taxSettings: this.taxSettings,
            vasPostingSettings: this.vasPostingSettings
          })
          this.$toast.success('Settings saved successfully.')
        } catch (err) {
          console.error('Error saving account settings in onSubmit:', err)
          this.$toast.error(err.response?.data?.message || 'Failed to save account settings.')
        }
      }
    },
    handleQuestions(val, index) {
      const status = this.catOptionTabs[index]
      if (!this.subCaseQuestions || Array.isArray(this.subCaseQuestions)) {
        this.subCaseQuestions = {}
      }
      this.$set(this.subCaseQuestions, status, val)
    },
    handleCloneAction(val, item, targetType) {
      this.clonedValue = val.value
      this.clonedKey = item
      this.cloneTarget = targetType

      if (Object.keys(this[targetType]).includes(item)) {
        this.showConfirmationClone = true
      } else {
        this.cloneNewQuestions()
      }
    },

    cloneWithExistingQuestions() {
      this[this.cloneTarget] = {
        ...this[this.cloneTarget],
        [this.clonedKey]: [...(this[this.cloneTarget][this.clonedKey] || []), ...this.clonedValue]
      }
      this.resetCloneState()
    },

    cloneNewQuestions() {
      this[this.cloneTarget] = {
        ...this[this.cloneTarget],
        [this.clonedKey]: this.clonedValue
      }
      this.resetCloneState()
    },

    resetCloneState() {
      this.showConfirmationClone = false
      this.clonedValue = null
      this.clonedKey = null
      this.cloneTarget = null
      this.$toast.success(`Questions copied successfully`)
    },
    removeDataCategory(item) {
      const index = this.addedDataCategories.indexOf(item)
      if (index !== -1) {
        this.addedDataCategories.splice(index, 1)
      }
    },
    handleFeedback(val, index) {
      const status = this.catOptionTabs[index]
      if (!this.customerFeedback || Array.isArray(this.customerFeedback)) {
        this.customerFeedback = {}
      }
      this.$set(this.customerFeedback, status, val)
    },
    async fetchBranches() {
      // Early return if branches are already loaded or currently loading
      if (this.branchesLoaded || this.loadingBranches) {
        return
      }

      this.loadingBranches = true
      try {
        const response = await this.$axios.get(axiosUrl.branches.getBranches)
        this.branches = response.data.branches
        this.branchesLoaded = true // Mark as loaded to prevent future calls
      } catch (error) {
        console.error('Error fetching branches:', error)
        this.$toast.error('Failed to load branches. Please try again.')
      } finally {
        this.loadingBranches = false
      }
    },
    onCancel() {
      this.orgKeys = []
      this.$emit('cancel')
      this.$refs.form.reset()
      // this.$refs.formAddress.reset();
      this.extraColumns = []
      this.categories = [
        {
          categoryName: '',
          options: [],
          optionsList: [],
          assignedTemplates: {},
          caseOutcomeFormId: {},
          customerFormId: {}
        }
      ]
    },
    addCategory() {
      this.categories.push({
        categoryName: '',
        options: [],
        optionsList: [],
        assignedTemplates: {},
        caseOutcomeFormId: {},
        customerFormId: {}
      })
    },
    checkForDuplicateAddressLine1() {
      const keyMap = new Map()

      this.addresses.forEach((address, index) => {
        const key = address.addressLine1.key

        if (key && keyMap.has(key)) {
          this.alertMessage = `Duplicate addressLine1 key found in Address ${
            keyMap.get(key) + 1
          } and Address ${index + 1}`
        } else {
          keyMap.set(key, index)
        }
      })
    },
    populateFields(subCase) {
      // console.log(this.useCaseLabel, 'useCaseLabel')
      if (!subCase) {
        this.$refs?.form?.reset()
        // this.orgKeys = []
        this.extraColumns = []
        this.categories = []
        return
      }

      this.subCaseQuestions = subCase?.questionnaire || {}
      this.customerFeedback = subCase?.userFeedbackQuestionnaire || {}
      const updatedOrgKeys = subCase.baseFieldMappings ? [...this.orgKeys] : this.useCaseLabel

      const fieldMappings = subCase.fieldMappings || {}
      const useCaseMapping = subCase.baseFieldMappings
        ? { ...subCase.baseFieldMappings, ...subCase[dynamicRequestPath[this.useCase]?.key] }
        : subCase.fieldMappings || {} // Add fallback empty object
      // console.log({ updatedOrgKeys })
      const addressesArray = subCase.customerFieldMappings?.addresses || []
      this.enableBranchMapping = subCase.branch ? true : false
      this.enableEntityMapping = subCase.entity ? true : false
      this.enableDataCategoryMapping = subCase.dataCategories?.length ? true : false
      this.selectedLoanTypes = (subCase.loanTypes || [])
        .filter((sl) => sl.enabled)
        .map((sl) => sl.type)
      this.enableLoanTypeMapping = this.selectedLoanTypes.length > 0
      this.selectedLoanCategories = (subCase.loanCategories || [])
        .filter((lc) => lc.enabled)
        .map((lc) => lc.type)
      this.loanCategoryOptions = [
        ...new Set([...this.loanCategoryOptions, ...this.selectedLoanCategories])
      ]
      this.enableLoanCategoryMapping = this.selectedLoanCategories.length > 0
      this.selectedLeadSources = (subCase.leadSource || [])
        .filter((ls) => ls.enabled)
        .map((ls) => ls.type)
      this.enableLeadSourceMapping = this.selectedLeadSources.length > 0
      this.caseOutcomeFormId = subCase.caseOutcomeFormId || {}
      this.customerFormId = subCase.customerFormId || {}

      updatedOrgKeys?.forEach((k) => {
        if (k) {
          const mapping = useCaseMapping[k.key] || {}
          this.$set(k, 'value', mapping.displayName || k.value || '')
          this.$set(
            k,
            'visibility',
            mapping.visible !== undefined ? mapping.visible : k.visibility || false
          )
          this.$set(
            k,
            'required',
            mapping.required !== undefined ? mapping.required : k.required || false
          )
          this.$set(
            k,
            'selectedPrefix',
            mapping.prefix !== undefined ? mapping.prefix : k.selectedPrefix || ''
          )
          this.$set(
            k,
            'selectedSuffix',
            mapping.suffix !== undefined ? mapping.suffix : k.selectedSuffix || ''
          )
          this.$set(
            k,
            'formVisibility',
            mapping.formVisibility !== undefined
              ? mapping.formVisibility
              : k.formVisibility || false
          )
          this.$set(
            k,
            'tableVisibility',
            mapping.tableVisibility !== undefined
              ? mapping.tableVisibility
              : k.tableVisibility || false
          )
          this.$set(
            k,
            'isAutoGenerated',
            mapping.isAutoGenerated !== undefined
              ? mapping.isAutoGenerated
              : k.isAutoGenerated || false
          )
        }
      })

      if (addressesArray.length) {
        const defaultAddressType = subCase.customerFieldMappings?.defaultAddress || ''
        this.addresses = addressesArray.map((address) => ({
          ...address,
          default: address.typeOfAddress === defaultAddressType
        }))
      }

      this.selectedBranch = subCase.branch || null
      this.entityName = subCase.entity?.displayName || 'Entity'
      this.entityEnabled = subCase.entity?.enabled
      this.orgKeys = updatedOrgKeys
      this.addedDataCategories = subCase.dataCategories?.map((cat) => cat.name) || []

      if (subCase.cloneIndex == 0 || subCase.cloneIndex) {
        this.caseType.value = ''
        this.caseType.icon = ''
      } else {
        this.caseType.value = subCase.name
        this.caseType.icon = subCase.icon || ''
      }
      this.caseOutcomeStatus =
        subCase.baseFieldMappings?.outcome?.displayName ||
        fieldMappings.caseOutcomeStatus?.displayName ||
        ''
      this.enableOutcomeColumn = subCase.baseFieldMappings?.outcome?.tableVisibility !== false
      this.caseStatusName =
        subCase.baseFieldMappings?.status?.displayName ||
        fieldMappings.status?.displayName ||
        fieldMappings.currentStatus?.displayName ||
        ''
      this.caseStatusesInput = subCase.caseStatuses?.length
        ? subCase.caseStatuses.map((cs) => cs.label)
        : ['Open', 'Closed', 'In-Progress']
      this.staticFields.crn =
        fieldMappings.crn?.displayName || subCase.customerFieldMappings?.crn?.displayName
      this.staticFields.customerFullName =
        fieldMappings.customerFullName?.displayName ||
        subCase.customerFieldMappings?.customerFullName?.displayName
      this.staticFields.customerMobile =
        fieldMappings.customerMobile?.displayName ||
        subCase.customerFieldMappings?.customerMobile?.displayName
      this.staticFields.customerEmail =
        fieldMappings.customerEmail?.displayName ||
        subCase.customerFieldMappings?.customerEmail?.displayName

      const accountSettingsObj =
        subCase?.loanManagementFieldMapping?.accountSettings || subCase?.accountSettings || {}
      const autoSettings =
        accountSettingsObj.loanAutoPostingSettings ||
        accountSettingsObj.autoPostingSettings ||
        subCase?.autoPostingSettings ||
        {}
      this.autoPostingSettings = {
        loanGroup: autoSettings.loanGroup || null,
        interest: autoSettings.interest || null,
        badDebts: autoSettings.badDebts || null,
        waiverLedger: autoSettings.waiverLedger || null,
        handLoanLpInterest: autoSettings.handLoanLpInterest || null,
        lpInterest: autoSettings.lpInterest || null,
        emiForwarding: autoSettings.emiForwarding || null,
        collectionCharges: autoSettings.collectionCharges || null,
        enablePrincipalBadDebtLedger: autoSettings.enablePrincipalBadDebtLedger || false,
        igstPayable: autoSettings.igstPayable || null,
        cgstPayable: autoSettings.cgstPayable || null,
        sgstPayable: autoSettings.sgstPayable || null,
        igstReceivable: autoSettings.igstReceivable || null,
        cgstReceivable: autoSettings.cgstReceivable || null,
        sgstReceivable: autoSettings.sgstReceivable || null
      }
      this.generalSettings =
        accountSettingsObj.generalLoanSettings ||
        accountSettingsObj.generalSettings ||
        subCase?.generalSettings ||
        {}
      let rawTax = accountSettingsObj.loanTaxSettings || {}
      let taxList = []
      Object.keys(rawTax).forEach((key) => {
        if (
          key !== '_id' &&
          key !== 'extendUseCaseId' &&
          key !== '__v' &&
          typeof rawTax[key] === 'object' &&
          rawTax[key] !== null
        ) {
          taxList.push({
            key,
            gstType: rawTax[key].gstType,
            roundOff: rawTax[key].roundOff
          })
        }
      })
      this.taxSettings = taxList.length
        ? taxList
        : accountSettingsObj.taxSettings || subCase?.taxSettings || []

      let rawVas = accountSettingsObj.loanVasPostingSettings || {}
      let vasList = []
      Object.keys(rawVas).forEach((key) => {
        if (
          key !== '_id' &&
          key !== 'extendUseCaseId' &&
          key !== '__v' &&
          typeof rawVas[key] === 'object' &&
          rawVas[key] !== null
        ) {
          vasList.push({
            key,
            accountId: rawVas[key]
          })
        }
      })
      this.vasPostingSettings = vasList.length
        ? vasList
        : accountSettingsObj.vasPostingSettings || subCase?.vasPostingSettings || []
      this.editModes = {
        loanGroup: false,
        interest: false,
        lpInterest: false,
        handLoanLpInterest: false,
        badDebts: false,
        waiverLedger: false,
        emiForwarding: false,
        collectionCharges: false
      }
      this.extraColumns = Object.keys(subCase.additionalColumns || {}).map((k) => {
        const column = subCase.additionalColumns[k]
        const displayName = column.displayName
        const fieldType = column.fieldType
        let value = fieldType ? `${displayName} - ${fieldType}` : `${displayName} - STRING`
        return {
          text: displayName,
          key: column.key,
          value: value,
          search: displayName,
          selectedPrefix: column.prefix,
          selectedSuffix: column.suffix,
          isAutoGenerated: column.isAutoGenerated || false,
          options: column.options ? column.options.join(', ') : '',
          formVisibility: column.formVisibility !== false,
          tableVisibility: column.tableVisibility !== false,
          required: column.required || false
        }
      })

      this.tableTabKeys = this.tableTabs.map((defaultTab) => {
        const customMapping = subCase?.tableTabMapping?.[defaultTab.key]
        return {
          text: customMapping?.text || defaultTab.text,
          key: defaultTab.key,
          value: customMapping?.displayName || defaultTab.value,
          icon: customMapping?.icon || defaultTab.icon || 'mdi-apps',
          visible: customMapping?.visible !== undefined ? customMapping.visible : true
        }
      })

      this.categories = (subCase.userDefinedCaseStatus || []).map((el) => ({
        categoryName: el.categoryName || 'Category 1',
        options: el.options || [],
        optionsList: el.optionsList || [],
        assignedTemplates: el.assignedTemplates || {},
        caseOutcomeFormId: el.caseOutcomeFormId || {},
        customerFormId: el.customerFormId || {}
      }))
    },
    checkDuplicateCaseType() {
      const existingSubCaseNames = this.$props.enabledSubCases.flatMap((sub) =>
        sub.subCase.map((s) => s.name.toLowerCase())
      )
      const isDuplicateCaseName = existingSubCaseNames.includes(this.caseType?.value?.toLowerCase())

      if (isDuplicateCaseName) {
        this.$toast.error('Case type name must be unique. A case with this name already exists.')
        return isDuplicateCaseName
      }
    },
    validateAddress() {
      this.checkForDuplicateAddressLine1()
    },
    getFilteredOutcomeQuestions(questions, currentKey) {
      return Object.fromEntries(Object.entries(questions).filter(([key]) => key !== currentKey))
    },

    // Icon selector methods
    getDisplayIcon(item) {
      // Ensure we always return a valid icon, with fallback
      const icon = item?.icon || 'mdi-apps'
      return icon
    },

    getCurrentEditingIcon() {
      if (!this.currentEditingItem || !this.currentEditingType) {
        return ''
      }
      return this.currentEditingItem.icon || ''
    },

    onIconSelected(selectedIcon) {
      if (this.currentEditingItem && this.currentEditingType === 'tableTab') {
        const iconValue = selectedIcon.icon || selectedIcon
        const tabKey = this.currentEditingItem.key

        // Update the current editing item
        this.$set(this.currentEditingItem, 'icon', iconValue)

        // Synchronize icon changes across all data structures
        this.syncIconChanges(tabKey, iconValue)

        // Emit event to parent component if needed
        this.$emit('icon-updated', {
          tabKey: tabKey,
          icon: iconValue,
          tabData: this.currentEditingItem
        })
      }

      // Reset editing state
      this.currentEditingItem = null
      this.currentEditingType = ''
      this.iconSelectorDialog = false
    },

    onIconCancelled() {
      // Reset editing state
      this.currentEditingItem = null
      this.currentEditingType = ''
      this.iconSelectorDialog = false
    },

    handleIconSelector(action, item) {
      if (action === 'open') {
        this.currentEditingItem = item
        this.currentEditingType = 'tableTab'

        // Set loading state for this specific button
        this.$set(this.iconButtonLoading, item.key, true)

        // Show dialog immediately with loading state
        this.iconSelectorDialog = true

        // Check if icons are already loaded to prevent redundant API calls
        if (this.iconsLoaded && this.apiIcons.length > 0) {
          this.iconSelectorLoading = false
          this.$set(this.iconButtonLoading, item.key, false)
        } else if (!this.loadingIcons) {
          // Show loading state in dialog
          this.iconSelectorLoading = true
          // Only fetch if not already loading
          this.fetchApiIcons()
            .then(() => {
              this.iconSelectorLoading = false
              this.$set(this.iconButtonLoading, item.key, false)
            })
            .catch(() => {
              this.iconSelectorLoading = false
              this.$set(this.iconButtonLoading, item.key, false)
            })
        } else {
          // Already loading, show loading state
          this.iconSelectorLoading = true
          // Wait for existing loading to complete
          const checkLoading = () => {
            if (!this.loadingIcons) {
              this.iconSelectorLoading = false
              this.$set(this.iconButtonLoading, item.key, false)
            } else {
              setTimeout(checkLoading, 100)
            }
          }
          checkLoading()
        }
      } else if (action === 'close' || action === 'cancel') {
        this.iconSelectorDialog = false
        this.currentEditingItem = null
        this.currentEditingType = ''
        this.iconSelectorLoading = false
        // Clear all button loading states
        this.iconButtonLoading = {}
      }
    },

    async fetchApiIcons() {
      // Early return if icons are already loaded or currently loading
      if (this.iconsLoaded || this.loadingIcons) {
        return Promise.resolve()
      }

      this.loadingIcons = true
      try {
        const res = await this.$axios.get('/mdi-icons')
        if (res.data && res.data.data && Array.isArray(res.data.data.i)) {
          this.apiIcons = res.data?.data?.i.map((icon) => {
            return {
              text: icon.n,
              icon: `mdi-${icon.n}`
            }
          })
          this.iconsLoaded = true // Mark as loaded to prevent future calls
        } else {
          throw new Error('Invalid API response format')
        }
      } catch (error) {
        console.error('Error fetching API icons:', error)
        this.$toast.error('Failed to load icons. Please try again.')
        throw error // Re-throw to handle in calling function
      } finally {
        this.loadingIcons = false
      }
    },

    // Preload icons silently in the background
    preloadIcons() {
      if (!this.iconsLoaded && !this.loadingIcons) {
        this.fetchApiIcons().catch((error) => {
          // Silent fail for background loading
          console.warn('Background icon preloading failed:', error)
        })
      }
    },

    syncIconChanges(tabKey, newIcon) {
      const tabIndex = this.tableTabKeys.findIndex((tab) => tab.key === tabKey)
      if (tabIndex !== -1) {
        // Use Vue's reactivity system properly - only update tableTabKeys
        this.$set(this.tableTabKeys[tabIndex], 'icon', newIcon)
      }

      // Force update to ensure UI reflects changes
      this.$nextTick(() => {
        this.$forceUpdate()
      })

      this.$toast.success('Icon updated successfully')
    },

    // Initialize tableTabKeys from static tableTabs
    initializeTableTabKeys() {
      // Deep clone tableTabs to avoid reference issues
      this.tableTabKeys = JSON.parse(JSON.stringify(this.tableTabs))
    },

    // Handle icon selection from DataMapping component
    onSubcaseIconSelected(selectedIcon) {
      // Store the selected icon for the subcase
      this.caseType.icon = selectedIcon

      // You can also emit an event to parent components if needed
      this.$emit('subcase-icon-selected', selectedIcon)
    },
    toggleAllLoanTypes() {
      this.$nextTick(() => {
        if (this.allLoanTypesSelected) {
          this.selectedLoanTypes = []
        } else {
          this.selectedLoanTypes = this.loanTypeOptions.slice()
        }
      })
    },
    toggleAllLeadSources() {
      this.$nextTick(() => {
        if (this.allLeadSourcesSelected) {
          this.selectedLeadSources = []
        } else {
          this.selectedLeadSources = this.leadSourceOptions.slice()
        }
      })
    },
    toggleAllLoanCategories() {
      this.$nextTick(() => {
        if (this.allLoanCategoriesSelected) {
          this.selectedLoanCategories = []
        } else {
          this.selectedLoanCategories = this.loanCategoryOptions.slice()
        }
      })
    },
    updateLoanCategoryOptions(val) {
      if (val && Array.isArray(val)) {
        val.forEach((item) => {
          if (item && typeof item === 'string' && !this.loanCategoryOptions.includes(item)) {
            this.loanCategoryOptions.push(item)
          }
        })
      }
    },
    formatUseCaseName(useCase) {
      if (!useCase) return ''
      return useCase.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
    },
    async fetchAccounts(search = '') {
      try {
        this.accountsLoading = true
        const response = await this.$axios.get(axiosUrl.accounts.getAccounts, {
          params: { name: search, limit: 50, isSpecific: false }
        })
        const results =
          response.data.accountsManagements ||
          response.data.accounts ||
          response.data.ledgerGroups ||
          []

        // Ensure that currently selected accounts are always kept in the list
        // so Vuetify displays their names correctly even after a new search is made.
        const selectedIds = [
          this.autoPostingSettings.loanGroup,
          this.autoPostingSettings.interest,
          this.autoPostingSettings.badDebts,
          this.autoPostingSettings.waiverLedger,
          this.autoPostingSettings.handLoanLpInterest,
          this.autoPostingSettings.lpInterest,
          this.autoPostingSettings.emiForwarding,
          this.autoPostingSettings.collectionCharges
        ].filter((id) => id && typeof id === 'string')

        const selectedAccounts = this.accountsList.filter((a) => selectedIds.includes(a._id))
        const resultsMap = new Map(results.map((r) => [r._id, r]))

        // Merge preserved selected accounts and new search results
        selectedAccounts.forEach((sa) => {
          if (!resultsMap.has(sa._id)) {
            results.push(sa)
          }
        })

        this.accountsList = results
      } catch (error) {
        console.error('Error fetching accounts:', error)
      } finally {
        this.accountsLoading = false
      }
    },
    debouncedFetchAccounts: null, // assigned in created()
    async fetchAllExtendUseCases() {
      try {
        const response = await this.$axios.get('/orgSetup/extend-usecase')
        this.allSubCases = response.data?.data || []
      } catch (error) {
        console.error('Error fetching subcases:', error)
      }
    }
  },
  watch: {
    addresses: {
      deep: true,
      handler() {
        this.checkForDuplicateAddressLine1()
      }
    },
    editSubCase: {
      async handler(subCase) {
        if (subCase) {
          // console.log({ subCase })
          this.populateFields(subCase)
        }
      },
      immediate: true
    },
    // cloneSubCase: {
    //   handler(subCase) {
    //     if (subCase) {
    //       console.log({ subCase })

    //       this.populateFields(subCase)
    //     }
    //   },
    //   immediate: true
    // },
    dynamicOrgKeys: {
      handler(newKeys) {
        if (newKeys.length > 0 && (!this.editSubCase || !this.orgKeys.length)) {
          const keys = this.useCase == 'COLLECTION' ? this.useCaseLabel : [...newKeys]
          keys.forEach((k) => {
            if (k) {
              this.$set(k, 'value', k.value || '')
              this.$set(k, 'visibility', k.visibility !== undefined ? k.visibility : false)
              this.$set(k, 'required', k.required !== undefined ? k.required : false)
              this.$set(k, 'selectedPrefix', k.selectedPrefix !== undefined ? k.selectedPrefix : '')
              this.$set(k, 'selectedSuffix', k.selectedSuffix !== undefined ? k.selectedSuffix : '')
              this.$set(
                k,
                'formVisibility',
                k.formVisibility !== undefined ? k.formVisibility : false
              )
              this.$set(
                k,
                'tableVisibility',
                k.tableVisibility !== undefined ? k.tableVisibility : false
              )
              this.$set(
                k,
                'isAutoGenerated',
                k.isAutoGenerated !== undefined ? k.isAutoGenerated : false
              )
            }
          })
          this.orgKeys = keys
        }
      },
      immediate: true
    },

    // Watch for initialization to set up tableTabKeys
    tableTabs: {
      handler() {
        // Initialize tableTabKeys only if it's empty (component initialization)
        if (this.tableTabKeys.length === 0) {
          this.initializeTableTabKeys()
        }
      },
      immediate: true
    },
    tableTabKeys: {
      deep: true,
      handler(newTabKeys) {
        // Force component update when tableTabKeys change to ensure UI consistency
        this.$nextTick(() => {
          this.$forceUpdate()
        })
      }
    }
  },
  created() {
    this.debouncedFetchAccounts = this.$debounce
      ? this.$debounce(this.fetchAccounts, 400)
      : (() => {
          let t
          return (val) => {
            clearTimeout(t)
            t = setTimeout(() => this.fetchAccounts(val), 400)
          }
        })()
  },
  async mounted() {
    await this.fetchAccounts()
    await this.fetchAllExtendUseCases()
    await this.fetchBranches()
    // Preload icons in background for better UX
    this.preloadIcons()
    // Initialize tableTabKeys from static tableTabs if empty
    if (this.tableTabKeys.length === 0) {
      this.initializeTableTabKeys()
    }
    this.subCaseQuestions = this.editSubCase?.questionnaire || []
    this.customerFeedback = this.editSubCase?.userFeedbackQuestionnaire || []
    this.checkForDuplicateAddressLine1()

    if (this.editSubCase?.crossLinkedSubCaseId) {
      this.enableCrossLinking = true
      this.crossLinkedSubCaseId = this.editSubCase.crossLinkedSubCaseId
    }
  }
}
</script>
<style scoped>
.custom-input-group {
  display: flex;
  align-items: center;
  padding: 2px;
  background-color: white;
}

.custom-input-group .v-select .v-input__control,
.custom-input-group .v-autocomplete .v-input__control {
  background-color: white !important;
}

.select-input {
  flex-grow: 1;
  width: 20%;
  /* font-size: smaller; */
}
.v-tab {
  font-size: 14px;
}

.text-input {
  flex-grow: 2;
  margin-left: 2px;
  width: 70%;
}
.tabtitle {
  text-transform: capitalize !important;
}
.expansion-panel {
  padding: 0px 15px 16px;
}
.card-title {
  padding-left: 0px !important;
  font-size: 20px Helvetica, Arial, sans-serif;
  color: #000000de;
}
.card-text {
  padding: 0px !important;
}

.col {
  padding: 0px !important;
}
</style>
